import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { Ball } from '../entities/Ball';
import { Brick, type BrickKind } from '../entities/Brick';
import { Paddle } from '../entities/Paddle';
import { AudioSystem } from '../systems/AudioSystem';
import { BeatClock } from '../systems/BeatClock';
import { CameraRig } from '../systems/CameraRig';
import { CollisionSystem, type FieldBounds } from '../systems/CollisionSystem';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { HeaderScene } from '../systems/HeaderScene';
import { Hud } from '../systems/Hud';
import { SampleLibrary, type LoadedSample } from '../systems/SampleLibrary';
import { createSeededRandom } from '../utils/random';
import { saveBlobUrl } from '../utils/download';

/** Tauri v2 fs plugin surface we consume — just enough to walk a picked folder
 *  from disk without triggering Chromium's showDirectoryPicker permission popup. */
type TauriFsEntry = { name: string; isDirectory: boolean; isFile: boolean };
type TauriFs = {
  readDir: (path: string) => Promise<TauriFsEntry[]>;
  readFile: (path: string) => Promise<Uint8Array>;
};

/** Join a picked root (absolute path) with relative segments, using whichever
 *  separator the root already uses so we stay consistent on Windows vs *nix. */
function joinFolderPath(root: string, segments: string[]): string {
  if (segments.length === 0) return root;
  const sep = root.includes('\\') ? '\\' : '/';
  return [root.replace(/[\\/]+$/, ''), ...segments].join(sep);
}

const FOLDER_AUDIO_EXT = /\.(wav|mp3|ogg|flac|m4a|aac|webm)$/i;

/** Portrait/phone layout gets a narrower + shorter field so cells render bigger
 *  on a small screen and the canvas fills the vertical space without a large
 *  empty gap above the sequencer. Chosen so 14 cols × 19 rows (visualizer 14/4
 *  + game 14/15) ≈ phone aspect. */
const IS_MOBILE_LAYOUT = typeof window !== 'undefined'
  && window.matchMedia('(max-width: 640px), (max-aspect-ratio: 3/4)').matches;

/** Everything snaps to this. Brick sizes, positions, and per-measure advance are all multiples. */
const GRID_UNIT = 1.4;
/** Playfield width in grid units. Sequencer stays at 16 steps regardless — brick
 *  hits arm the CURRENT playhead step, so brick column count is decoupled from
 *  sequencer length. Widen the field for more visual play area. */
const GRID_COLS = IS_MOBILE_LAYOUT ? 16 : 20;
const BRICK_ROWS = 4;
/** Total field height in grid rows (bricks + play area). Mobile adds a row
 *  so the visualizer above shrinks proportionally (canvas is exact
 *  --cell × field_rows, visualizer flex-grows into the residual). */
const FIELD_ROWS = IS_MOBILE_LAYOUT ? 15 : 16;

const FIELD: FieldBounds = {
  halfWidth: (GRID_COLS * GRID_UNIT) / 2,
  top: -9.2,
  bottom: -9.2 + FIELD_ROWS * GRID_UNIT,
};
/** Grid column where the leftmost cell begins. */
const GRID_ORIGIN_X = -GRID_COLS * GRID_UNIT / 2;
/** Top row's centre-Z. Sits flush against the top of the field. */
const GRID_ORIGIN_Z = -9.2 + GRID_UNIT / 2;

const ROW_COLORS = [
  { color: '#ff1e5a', emissive: '#5a0820' },
  { color: '#ff6a1a', emissive: '#5a1e08' },
  { color: '#ffd60a', emissive: '#4d3300' },
  { color: '#26e884', emissive: '#0a4a2a' },
  { color: '#3d5cff', emissive: '#0a1a5a' },
  { color: '#b64bff', emissive: '#320a5a' },
];

/** Which procedural sample plays for each brick row (system mode). */

/** Row labels used when the player loads their own folder — samples are random per brick,
 *  so the row identity is just a track number. */
const FOLDER_ROW_LABELS = ['01', '02', '03', '04', '05', '06'] as const;

/** Fixed reference tempo used to scale ball speed & chaos. Doubling BPM = doubling ball speed. */
const REFERENCE_BPM = 80;
/** Score interval that awards a bonus life. Classic arcade extend — reads as
 *  a reward for long runs and softens the game-over cliff. */
const EXTRA_LIFE_STEP = 25000;

/** Combo window: a fresh brick destruction within this many seconds AND
 *  within this many world units of the previous kill chains the combo. */
const COMBO_WINDOW_S = 1.2;
const COMBO_RADIUS = 3.5;
const COMBO_MAX = 5;

/** Playback-rate pools indexed by procedural slot (0-3 percussive, 4-5 tonal).
 *  Bass + lead lock to the same major pentatonic root so anything the player
 *  builds stays in key. Bass gets a tighter pool (root/fifth/octave only) so
 *  it sits as a foundation under the lead's melodic pentatonic lines instead
 *  of noodling in the same range. Indexed by slot instead of name so
 *  Randomize can rename slots (kick → boom) without breaking pitch selection. */
const MAJOR_PENTATONIC = [1.0, 1.125, 1.25, 1.5, 1.667, 2.0] as const;
const ROW_RATE_POOLS: readonly (readonly number[])[] = [
  [1.0],             // slot 0 — kick-role
  [1.0],             // slot 1 — snare-role
  [1.0, 1.25, 1.5],  // slot 2 — hat-role
  [1.0, 1.5, 2.0],   // slot 3 — perc-role
  MAJOR_PENTATONIC,  // slot 4 — bass (full pentatonic, walks with the lead)
  MAJOR_PENTATONIC,  // slot 5 — lead (full pentatonic for melody)
];

type Phase = 'ready' | 'playing' | 'paused' | 'lifePause' | 'finishing' | 'won' | 'lost';

/** Queued until the next beat so hits land on the grid. */
type PendingHit = {
  brickId: number;
  kind: BrickKind;
  sample: LoadedSample | null;
  loopKey: string;
  /** Desired loop state after this hit (loop bricks only). */
  loopDesired: boolean | null;
  destroyed: boolean;
  sampleName: string;
};

/** Armed loop brick that re-triggers (or sustains) on every beat. */
type ArmedLoop = {
  key: string;
  sample: LoadedSample;
  brickId: number;
  /** Long buffers free-run; short ones re-fire each beat. */
  freeRun: boolean;
};

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private composer!: EffectComposer;
  private bloomPass!: UnrealBloomPass;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 80);
  private readonly input: InputController;
  private readonly paddle = (() => {
    const p = new Paddle();
    // Anchor the paddle back-edge on FIELD.bottom regardless of field height.
    p.setZ(FIELD.bottom - p.halfDepth);
    return p;
  })();
  private readonly ball = new Ball();
  private readonly bricks: Brick[] = [];
  private readonly collision = new CollisionSystem();
  private readonly library = new SampleLibrary();
  private readonly audio = new AudioSystem(this.library);
  private readonly clock = new BeatClock(100, 180, 0.45);
  private readonly hud = new Hud();
  /** Sequencer BPM input, captured during setup so the update loop can
   *  push the game's current BPM back into it (two-way sync with the HUD). */
  private seqBpmInput: HTMLInputElement | null = null;
  private readonly cameraRig = new CameraRig(this.camera, (FIELD.top + FIELD.bottom) / 2);
  private readonly loop = new Loop(
    (delta, elapsed) => this.update(delta, elapsed),
    () => this.render(),
  );

  private readonly tuning: DebugTuning = {
    startBpm: 80,
    bpmPerRound: 15,
    baseBallSpeed: 19,
    paddleSpeed: 22,
    exposure: 0.75,
    maxDpr: 2,
    noteShelfLife: 8,
    // Header depth-of-field. Focus at the camera→origin distance keeps the
    // countdown text sharp; low aperture keeps the near→far falloff subtle.
    bokehFocus: 13.1,
    bokehAperture: 0.0045,
    bokehMaxBlur: 0.010,
    gameBloomStrength: 0.06,
    gameBloomRadius: 0.30,
    gameBloomThreshold: 0.25,
    headerBloomStrength: 0.02,
    headerBloomRadius: 0.39,
    headerBloomThreshold: 0.88,
  };

  /** How far the brick grid steps toward the paddle when it advances. */
  private readonly measureAdvance = GRID_UNIT;
  /** Advance every N measures. 4 = one grid cell every 16 beats. */
  private readonly advanceEveryMeasures = 4;
  /** 16-step (one-bar of 16ths) sequencer that arms as you break bricks. */
  private readonly sequencerSteps = 16;
  /** sequencer[row][step] = the sample + rate armed at that instrument row + step,
   *  plus `remaining` = triggers left before the note auto-clears. `isStatic`
   *  marks user-authored notes (clicked directly on the sequencer grid) that
   *  should persist indefinitely — they skip the shelf-life decrement so the
   *  player can compose a beat while brick-hits fill in around them. */
  private readonly sequencer: (
    | { sample: LoadedSample; rate: number; remaining: number; isStatic?: boolean }
    | null
  )[][] = Array.from({ length: 6 }, () => Array(16).fill(null));
  private lastFiredSixteenth = -1;
  /** Measure index (16ths / sequencerSteps) at last tick — when it changes,
   *  we rotate the sample-mode row → slice map so the 6 visible slot labels
   *  cycle through the loaded pool instead of holding on the same slices. */
  private lastMeasureIndex = -1;
  /** Z past which advancing bricks steal a life. Sits at the paddle's top
   *  edge (paddle depth = 0.7 = ½ GRID_UNIT) so bricks visibly reach the
   *  final row and touch the paddle before the game ends — previously the
   *  breach zone was a full cell above the paddle, so bricks stopped early. */
  private readonly dangerZ = FIELD.bottom - 0.7;

  private readonly debugTools: DebugTools;
  private readonly world = new THREE.Group();
  private readonly pendingHits: PendingHit[] = [];
  private readonly armedLoops = new Map<string, ArmedLoop>();
  private frame = 0;
  private score = 0;
  private lives = 3;
  /** Next score threshold that awards a bonus life. Bumps by EXTRA_LIFE_STEP
   *  each award so long runs keep earning lives. Reset in resetRun. */
  private nextLifeAt = EXTRA_LIFE_STEP;
  /** Combo chain: consecutive brick destructions within COMBO_WINDOW_S and
   *  COMBO_RADIUS of each other. Multiplies awarded points to reward carom
   *  shots that clear clusters. Reset on paddle contact, life loss, and
   *  timeout in awardDestruction. */
  private combo = 0;
  private lastKillTime = 0;
  private lastKillX = 0;
  private lastKillZ = 0;
  private round = 1;
  private phase: Phase = 'ready';
  /** True when the game is waiting for the user to click a "Start Game"
   *  button on the initial overlay. Non-mobile flows use this instead of
   *  auto-running the countdown so users can explore sounds first. Mobile
   *  keeps its full-screen splash which serves the same "opt in to start"
   *  purpose and doubles as the audio-context unlock gesture. */
  /** True until the player explicitly starts a new run via the "Ready to
   *  play?" overlay button. Set on all surfaces (desktop, Tauri, mobile) so
   *  the game never auto-launches straight into a countdown — mobile used
   *  to skip this and dropped players into a countdown right after the
   *  splash, with no chance to explore the sound panels first. */
  private awaitingStart = true;
  private status = 'Move paddle · Space / click to launch';
  private shake = 0;
  private bricksTotal = 0;
  private beatInBar = 0;
  private rng = createSeededRandom(1);
  private pausedForScreenshot = false;
  /** Wall-clock ms when lifePause was entered — drives the ball-blink animation. */
  private lifePauseStart = 0;
  private reducedMotion = false;
  private unbindLibrary: (() => void) | null = null;

  /** Live 3D scene mounted above the grid — floating instrument avatars,
   *  shatter bursts on each brick hit. */
  private headerScene: HeaderScene | null = null;
  /** Set true when the 3-2-1 countdown finishes; the next 'ready' tick
   *  auto-launches the ball so the player never has to press Space. */
  private autoLaunchQueued = false;
  /** True while the 3-2-1 → NOIZ countdown is running. Blocks manual launch
   *  input so the player can't jump the gun. */
  private countdownRunning = false;
  /** ARKANOIZ title flash fires once on the first song complete. Subsequent
   *  rounds get the LEVEL banner instead so the two don't overlap. */
  private titleFlashed = false;
  /** True when the game paused itself because a UI panel (sound/settings)
   *  opened, so we know to auto-resume when the last panel closes. */
  private autoPausedByPanel = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;

    const launchButton = document.querySelector<HTMLElement>('#launch-button');
    // Include the whole game stack as a pointerdown surface so touches that
    // land in the mobile thumb-rest strip (padding below the canvas) still
    // start paddle drag.
    const stackSurface = document.querySelector<HTMLElement>('#game-stack');
    this.input = new InputController(canvas, launchButton, stackSurface);

    this.debugTools = new DebugTools(this.tuning, () => {
      this.renderer.toneMappingExposure = this.tuning.exposure;
      resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr, FIELD.halfWidth, FIELD_ROWS * GRID_UNIT);
      this.headerScene?.setBokeh({
        focus: this.tuning.bokehFocus,
        aperture: this.tuning.bokehAperture,
        maxblur: this.tuning.bokehMaxBlur,
      });
      if (this.bloomPass) {
        this.bloomPass.strength = this.tuning.gameBloomStrength;
        this.bloomPass.radius = this.tuning.gameBloomRadius;
        this.bloomPass.threshold = this.tuning.gameBloomThreshold;
      }
      this.headerScene?.setBloom({
        strength: this.tuning.headerBloomStrength,
        radius: this.tuning.headerBloomRadius,
        threshold: this.tuning.headerBloomThreshold,
      });
    });

    this.scene.add(this.world);
    this.createScene();
    this.cameraRig.snap();
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr, FIELD.halfWidth, FIELD_ROWS * GRID_UNIT);

    // Post-processing: subtle uniform haze across the whole scene — no
    // hot-spot bloom, so paddle + bricks stay readable and the effect
    // reads as film-lens softness rather than glow blooms.
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    // Threshold raised from 0 → 0.6 so only actually-bright pixels bloom.
    // Live-tunable from the settings panel via applyBloomSettings.
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(1024, 1024),
      this.tuning.gameBloomStrength,
      this.tuning.gameBloomRadius,
      this.tuning.gameBloomThreshold,
    );
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(new OutputPass());
    this.composer.setSize(this.renderer.domElement.width, this.renderer.domElement.height);

    const headerCanvas = document.querySelector<HTMLCanvasElement>('#header-canvas');
    if (headerCanvas) {
      this.headerScene = new HeaderScene(headerCanvas, { showTitle: false });
      // Mobile header aspect is much narrower than desktop, so the 20-unit-
      // wide countdown plane runs off both edges at 1.0 scale. Shrink the
      // baseline so "MAKE SOME NOIZ!" fits within the visualizer canvas.
      if (IS_MOBILE_LAYOUT) {
        this.headerScene.setHeaderCardTransform(-0.491, 0.78);
      }
    }

    this.installUiHandlers();
    this.syncSamplePanels();
    this.installTestHooks();
    void this.bootstrapSamples();
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  startCountdown(): void {
    this.countdownRunning = true;
    this.headerScene?.startCountdown(() => {
      this.autoLaunchQueued = true;
      this.countdownRunning = false;
      // Kick recording at the "MAKE SOME NOIZ!" reveal so the countdown's
      // final beat lands in the captured video, not after the ball launches
      // one frame later.
      void this.autoStartRecording();
    });
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.audio.dispose();
    this.debugTools.dispose();
    this.unbindLibrary?.();
    for (const brick of this.bricks) brick.dispose();
    this.paddle.dispose();
    this.ball.dispose();
    this.renderer.dispose();
    this.headerScene?.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private async bootstrapSamples(): Promise<void> {
    await this.audio.unlock();
    await this.library.loadFromPublicFolder();
    await this.library.ensureProceduralFallback((data) => this.audio.decode(data));
    // Fresh boot: randomize the system sounds + NOIZ FX so every session sounds different.
    await this.library.randomizeSystemSounds(this.rng);
    this.randomizeFxSliders();
    this.rebuildBricks();
    this.resetBallOnPaddle();
    this.randomizePaddleColor();
    this.refreshSampleUi();
    this.status = 'Samples ready · Space / click to launch';
    // Countdown is user-triggered on load. Mobile clears its splash first
    // (main.ts kicks startCountdown from the Start button); desktop shows a
    // "Ready to play?" overlay (awaitingStart) with a Start Game button that
    // does the same. Skips the jarring auto-start so players can browse the
    // sounds / sequencer panels before committing.
    const splashOpen = document.querySelector('#mobile-splash[data-open="1"]');
    if (!splashOpen && !this.awaitingStart) {
      this.startCountdown();
    }
  }

  private randomizePaddleColor(): void {
    // Locked to the mint accent — random rolls landed on purple / dark rows
    // that were hard to see on mobile against the dark bg. Kept the method
    // name for the two existing call sites (start + reset).
    this.paddle.setColor('#7cf0c2', '#0e3a2a');
  }

  private randomizeFxSliders(): void {
    const fxKeys = ['bit', 'ott', 'delay', 'crunch'] as const;
    for (const key of fxKeys) {
      // Bias initial rolls toward mid-low so first play doesn't sound over-processed.
      const value = Math.round(this.rng() * 60) / 100;
      this.audio.setNoizFx({ [key]: value });
      for (const prefix of ['fx-', 'fx3-']) {
        const input = document.querySelector<HTMLInputElement>(`#${prefix}${key}`);
        if (input) input.value = String(value);
      }
    }
  }

  private currentBallSpeed(): number {
    // Ball speed follows BPM but on a softer curve — level 1 (baseline) is
    // unchanged, higher rounds scale at 0.6× the BPM ramp so level 5+ stays
    // playable instead of matching the sequencer's full tempo growth.
    const bpmRatio = this.clock.bpm / REFERENCE_BPM;
    const softened = 1 + Math.max(0, bpmRatio - 1) * 0.6;
    return this.tuning.baseBallSpeed * softened;
  }

  private currentRoundBpm(): number {
    return this.tuning.startBpm + (this.round - 1) * this.tuning.bpmPerRound;
  }

  private update(delta: number, elapsed: number): void {
    this.frame += 1;
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }

    if (resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr, FIELD.halfWidth, FIELD_ROWS * GRID_UNIT)) {
      this.composer?.setSize(this.renderer.domElement.width, this.renderer.domElement.height);
    }

    // Keep the clock live from the settings dial. Both startBpm (used by the
    // clock's internal ramp math) and bpm (the *current* tempo — drives ball
    // speed, chaos, sequencer timing) track the tuning + round.
    this.clock.startBpm = this.tuning.startBpm;
    if (
      this.phase === 'playing' ||
      this.phase === 'lifePause' ||
      this.phase === 'finishing'
    ) {
      this.clock.bpm = this.currentRoundBpm();
    }
    if (this.input.consumeRestart() && this.phase !== 'finishing') {
      this.resetRun();
    }

    // Capture the OS cursor while the run is live so players don't accidentally
    // drag off the canvas onto the SAMPLE panels. Released the moment we pause,
    // win, lose, or return to the Ready overlay — Esc/panel-open both flow
    // through the pause path, which unsets this and shows the cursor again.
    const runLive =
      !this.awaitingStart &&
      this.phase !== 'paused' &&
      this.phase !== 'won' &&
      this.phase !== 'lost';
    this.input.setPointerLockAllowed(runLive);

    // Freeze paddle when the song is over OR paused so pointer movement doesn't
    // slide it around while the user is trying to click overlay buttons. Also
    // pin it dead center while the "Ready to play?" overlay is up so the paddle
    // isn't already drifting away from the ball when the countdown fires.
    if (this.awaitingStart) {
      this.paddle.setX(0);
    } else if (this.phase !== 'won' && this.phase !== 'lost' && this.phase !== 'paused') {
      this.updatePaddle(delta);
    }

    if (this.phase === 'ready' && this.awaitingStart) {
      this.ball.resetOnPaddle(this.paddle.x, this.paddle.z, this.paddle.halfDepth);
      // Waiting for the user to click "Start Game" — drain any queued Space
      // or canvas taps so they don't launch the ball the moment we transition
      // out of this state. Space also acts as a Start shortcut.
      const spaceStart = this.input.consumePause();
      this.input.consumeLaunch();
      if (spaceStart) {
        this.awaitingStart = false;
        this.syncGameOverlay();
        this.startCountdown();
      }
    } else if (this.phase === 'ready') {
      this.ball.resetOnPaddle(this.paddle.x, this.paddle.z, this.paddle.halfDepth);
      // Drain the launch queue but only honor it once the countdown is done —
      // otherwise a keen player could Space through the 3-2-1 and skip the intro.
      const manualLaunch = this.input.consumeLaunch();
      const launched = (manualLaunch && !this.countdownRunning) || this.autoLaunchQueued;
      if (launched) {
        this.autoLaunchQueued = false;
        void this.audio.unlock();
        this.clock.reset(this.currentRoundBpm());
        this.ball.mesh.visible = true;
        this.ball.launch(this.rng() * 0.8 - 0.4, this.currentBallSpeed());
        this.phase = 'playing';
        this.status = `Round ${this.round} · ${Math.round(this.currentRoundBpm())} BPM · go`;
        // Auto-start recording on the first launch so the whole play session is captured.
        void this.autoStartRecording();
      }
    } else if (this.phase === 'playing') {
      if (this.input.consumePause()) {
        // Space during active play → pause everything (ball, clock, sequencer, loops).
        // Clicks intentionally do NOT pause — you can click freely while playing.
        this.input.consumeLaunch(); // drain any pointer-click that fired the same frame
        this.audio.stopAllLoops();
        this.armedLoops.clear();
        for (const brick of this.bricks) brick.loopActive = false;
        this.phase = 'paused';
        this.status = 'Paused · Space to resume';
        // Pin the NOIZ card while paused so we have a stable focus target
        // to check the DoF sharp plane against. Released on resume.
        this.headerScene?.setHeaderEditFrozen(true);
      } else {
        this.input.consumeLaunch(); // clicks during play should not queue a stale launch
        this.stepBall(delta);
        this.syncBallSpeedToBpm();
      }
    } else if (this.phase === 'paused') {
      // Everything held. Space (or click) resumes.
      if (this.input.consumeLaunch()) {
        // Drain any queued pause too — Space keydown queues BOTH launch and
        // pause, so without this drain the next 'playing' frame sees the
        // stale pause queue and immediately re-pauses, which re-pins the
        // "MAKE SOME NOIZ" card.
        this.input.consumePause();
        this.phase = 'playing';
        this.status = 'Back in';
        this.headerScene?.setHeaderEditFrozen(false);
      }
    } else if (this.phase === 'lifePause') {
      // Life lost mid-run — keep the song looping while the ball waits on the paddle.
      // Space / click relaunches without resetting the clock so the beat stays continuous.
      this.ball.resetOnPaddle(this.paddle.x, this.paddle.z, this.paddle.halfDepth);
      // Blink the ball 3 times over ~900 ms so the player sees where it respawns.
      const blinkElapsed = performance.now() - this.lifePauseStart;
      const blinkDuration = 900;
      const halfCycle = 150; // 3 flashes = 6 half-cycles × 150 ms
      this.ball.mesh.visible =
        blinkElapsed >= blinkDuration || Math.floor(blinkElapsed / halfCycle) % 2 === 0;
      if (this.input.consumeLaunch()) {
        // Snap fully visible in case launch happens mid-blink.
        this.ball.mesh.visible = true;
        this.ball.launch(this.rng() * 0.8 - 0.4, this.currentBallSpeed());
        this.phase = 'playing';
        this.status = `${this.lives} left · back in`;
      }
    } else if (this.phase === 'finishing') {
      // Ball is dead but let the song ring out into the recording — ignore input.
      this.ball.mesh.visible = false;
    } else if (this.phase === 'won' || this.phase === 'lost') {
      const songComplete = !this.bricks.some((b) => b.alive);
      const canContinue =
        this.phase === 'lost' && songComplete && this.lastStopReason !== 'stopped';
      // Game Over from losing all lives (song didn't finish) — require the
      // on-screen Play Again button. Space presses queued during the finishing
      // wrap-up window were auto-restarting the run before the overlay could
      // read, so drain them here and let the button click handler restart.
      const gameOverModal = this.phase === 'lost' && !songComplete;
      if (gameOverModal) {
        this.input.consumeLaunch();
        this.input.consumeRestart();
        this.input.consumePause();
      } else if (this.input.consumeLaunch() || this.input.consumeRestart()) {
        // Song complete (all bricks cleared, wasn't a manual stop) → treat
        // Space/R like the on-screen Continue button so tempo keeps ramping
        // instead of silently resetting the run to Round 1 / 80 BPM.
        if (canContinue) {
          this.advanceToNextRound();
        } else {
          this.resetRun();
          // Fresh start from a finished run — clear the sequencer so the next
          // song builds up from scratch.
          this.clearSequencer();
        }
      }
    }

    // Transport runs during 'playing', 'lifePause', and 'finishing' so the song
    // keeps looping between lives and cleanly captures its last bar on death.
    // Initial 'ready' + terminal 'won'/'lost' pause the sequencer.
    if (
      this.phase === 'playing' ||
      this.phase === 'lifePause' ||
      this.phase === 'finishing'
    ) {
      // Kill any standalone Play-button preview once the game clock takes
      // over — otherwise both clocks fire the same notes off-phase.
      if (this.previewIntervalId !== null) {
        window.clearInterval(this.previewIntervalId);
        this.previewIntervalId = null;
        const playBtn = document.querySelector<HTMLButtonElement>('#seq-play');
        if (playBtn) playBtn.textContent = '▶ Play';
      }
      const firedBeats = this.clock.update(delta, false);
      for (const beatIndex of firedBeats) {
        this.onBeat(beatIndex);
      }
      this.tickSequencer();
    }


    const animElapsed = this.reducedMotion ? 0 : elapsed;
    for (const brick of this.bricks) brick.updateVisual(animElapsed);

    // Subtle ball pulse locked to beat phase.
    if (!this.reducedMotion && this.ball.launched) {
      const pulse = 1 + (1 - this.clock.phase) * 0.08;
      this.ball.mesh.scale.setScalar(pulse);
    } else {
      this.ball.mesh.scale.setScalar(1);
    }
    this.ball.updateGlow();
    this.paddle.updateGlow();

    this.shake = Math.max(0, this.shake - delta * 4);
    this.cameraRig.update(delta, this.reducedMotion ? 0 : this.shake);

    this.hud.update({
      score: this.score,
      bricksLeft: this.bricks.filter((b) => b.alive).length,
      bricksTotal: this.bricksTotal,
      lives: this.lives,
      bpm: this.clock.bpm,
      beatInBar: this.beatInBar,
      status: this.status,
    });
    // Mirror the HUD's live BPM into the sequencer input so a round-advance
    // or clock.reset shows up in both readouts. Skip while the input is
    // focused so mid-typing doesn't get clobbered.
    if (this.seqBpmInput && document.activeElement !== this.seqBpmInput) {
      const shown = String(Math.round(this.clock.bpm));
      if (this.seqBpmInput.value !== shown) this.seqBpmInput.value = shown;
    }
    this.syncGameOverlay();
    this.syncPauseIcon();
    this.headerScene?.update(delta);
    this.publishDiagnostics();
  }

  /** Pause the game while any UI drawer (sound / settings) is open so the
   *  ball doesn't keep moving behind the panels. Resumes automatically once
   *  the last panel closes, but only if we were the ones who paused it. */
  private syncPanelPause(): void {
    const appEl = document.querySelector<HTMLElement>('#app');
    const anyPanelOpen =
      appEl?.getAttribute('data-sound-open') === '1' ||
      appEl?.getAttribute('data-settings-open') === '1';
    if (anyPanelOpen && this.phase === 'playing') {
      this.audio.stopAllLoops();
      this.armedLoops.clear();
      for (const brick of this.bricks) brick.loopActive = false;
      this.phase = 'paused';
      this.status = 'Paused · close panel to resume';
      this.headerScene?.setHeaderEditFrozen(true);
      this.autoPausedByPanel = true;
    } else if (!anyPanelOpen && this.autoPausedByPanel && this.phase === 'paused') {
      this.autoPausedByPanel = false;
      this.phase = 'playing';
      this.status = 'Back in';
      this.headerScene?.setHeaderEditFrozen(false);
    }
  }

  private syncPauseIcon(): void {
    const btn = document.querySelector<HTMLButtonElement>('#hud-pause');
    if (!btn) return;
    const isPaused = this.phase === 'paused';
    // Inline SVG (not emoji) — Android/Brave rendered ⏸ as an orange color-
    // emoji tile even with \uFE0E, so use vector paths for guaranteed
    // monochrome presentation on every platform.
    const nextGlyph = isPaused
      ? '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M4 3v10l9-5z"/></svg>'
      : '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M4 3h3v10H4zM9 3h3v10H9z"/></svg>';
    if (btn.innerHTML !== nextGlyph) btn.innerHTML = nextGlyph;
    const nextLabel = isPaused ? 'Resume' : 'Pause';
    if (btn.title !== nextLabel) btn.title = nextLabel;
  }

  private syncGameOverlay(): void {
    const overlay = document.querySelector<HTMLElement>('#game-overlay');
    const title = document.querySelector<HTMLElement>('#game-overlay-title');
    const sub = document.querySelector<HTMLElement>('#game-overlay-sub');
    const actions = document.querySelector<HTMLElement>('#game-overlay-actions');
    const dlAudio = document.querySelector<HTMLButtonElement>('#overlay-download-audio');
    const dlVideo = document.querySelector<HTMLButtonElement>('#overlay-download-video');
    if (!overlay || !title || !sub) return;
    let content: { title: string; sub: string } | null = null;
    if (this.awaitingStart) {
      content = { title: 'Ready to play?', sub: 'Explore the sounds, then hit Start' };
    } else if (this.phase === 'paused') {
      content = { title: 'Paused', sub: 'Space to resume' };
    } else if (this.phase === 'finishing') {
      // Suppress the "wrapping up…" overlay during the ~1.5s finalize window.
      // The user only cares about the end-state card (Continue + Save Audio/
      // Video buttons); showing an intermediate message reads as three back-
      // to-back modals for a single event.
      content = null;
    } else if (this.phase === 'lost') {
      const songComplete = !this.bricks.some((b) => b.alive);
      const scoreLine = `Score: ${this.score}`;
      if (this.lastStopReason === 'stopped') {
        content = this.recordingUrl
          ? { title: 'That was it? 🤷', sub: `${scoreLine} · You didn't finish the song, maybe next time · Take what you got or Space to try again` }
          : { title: 'That was it? 🤷', sub: `${scoreLine} · You didn't finish the song, maybe next time · Space to try again` };
      } else if (songComplete) {
        // Always show the completion card the moment the phase transitions to
        // 'lost' — waiting for recordingUrl was fine when finalization always
        // succeeded, but on rounds where recording drops (autoStartRecording
        // was skipped or the finalize errored) the overlay never appeared.
        // Show Continue immediately; the sub-line advertises Download once
        // the URL lands.
        content = this.recordingUrl
          ? { title: 'You finished a song! 🎉', sub: `${scoreLine} · Download your track · Space/Esc to continue` }
          : { title: 'You finished a song! 🎉', sub: `${scoreLine} · Space/Esc to continue` };
      } else {
        // "Game Over!" title with an encouraging score line under it.
        // Bump the flavor line once tempo passes 150 BPM (round 5+ with
        // default settings) — surviving that long earns "Nice work" instead
        // of the softer default.
        const flavor = this.clock.bpm >= 150 ? 'Nice work.' : 'Not bad.';
        content = {
          title: 'Game Over!',
          sub: `<span class="overlay-score">${this.score}</span> points! ${flavor}`,
        };
      }
    } else if (this.phase === 'won') {
      content = { title: 'Stage clear', sub: 'Space to continue' };
    }
    if (content) {
      // innerHTML so the game-over sub can inline the score in an accent
      // span. Every other content string is plain text — safe because we
      // control the source (no user input flows into these strings).
      title.innerHTML = content.title;
      sub.innerHTML = content.sub;
      overlay.removeAttribute('hidden');
    } else {
      overlay.setAttribute('hidden', '');
    }
    // Actions row shows when the run is over AND we have something to save OR a completed song to continue from.
    const runOver = this.phase === 'lost' || this.phase === 'won';
    const songComplete = !this.bricks.some((b) => b.alive);
    const showContinue = this.phase === 'lost' && songComplete && this.lastStopReason !== 'stopped';
    const showPlayAgain = this.phase === 'lost' && !songComplete;
    const showDownloads = runOver && (!!this.recordingUrl || !!this.videoUrl);
    if (actions) actions.toggleAttribute('hidden', !(showDownloads || showContinue || showPlayAgain));
    if (dlAudio) dlAudio.hidden = !this.recordingUrl;
    if (dlVideo) dlVideo.hidden = !this.videoUrl;
    const songContinueBtn = document.querySelector<HTMLButtonElement>('#overlay-song-continue');
    if (songContinueBtn) songContinueBtn.hidden = !showContinue;
    const playAgainBtn = document.querySelector<HTMLButtonElement>('#overlay-play-again');
    if (playAgainBtn) playAgainBtn.hidden = !showPlayAgain;
    // Paused-actions row shows only when phase === 'paused' (End / Continue).
    const pausedActions = document.querySelector<HTMLElement>('#game-overlay-paused-actions');
    pausedActions?.toggleAttribute('hidden', this.phase !== 'paused' || this.awaitingStart);
    // Ready-actions row shows only on the initial "click Start to play" state.
    const readyActions = document.querySelector<HTMLElement>('#game-overlay-ready-actions');
    readyActions?.toggleAttribute('hidden', !this.awaitingStart);
    // Reveal the system cursor over the canvas whenever the game isn't actively
    // playing so users can click overlay buttons without hunting for their pointer.
    const cursorOn = runOver || this.phase === 'paused' || this.awaitingStart;
    const app = document.querySelector<HTMLElement>('#app');
    app?.classList.toggle('run-over', cursorOn);
  }

  private onBeat(beatIndex: number): void {
    this.beatInBar = ((beatIndex % 4) + 4) % 4;
    const downbeat = this.beatInBar === 0;
    const when = this.audio.now() + 0.02;
    const chaos = Math.max(0, this.clock.bpm / REFERENCE_BPM - 1);

    // Downbeat + when captured for the pending-hit resolver below.
    void downbeat;

    // Flush brick hits queued since last beat.
    const batch = this.pendingHits.splice(0, this.pendingHits.length);
    for (const hit of batch) {
      this.resolvePendingHit(hit, when, chaos);
    }

    // Armed loop bricks re-trigger every 4 bars (16 beats). Sparse so they never pile up.
    const isBarLine = (((beatIndex % 16) + 16) % 16) === 0;
    if (isBarLine) {
      for (const armed of this.armedLoops.values()) {
        if (armed.freeRun) continue;
        const rate = 1 + (this.rng() * 2 - 1) * (0.03 + chaos * 0.4);
        this.audio.playBeatVoiceAt(armed.key, armed.sample, when, rate);
      }
    }

    // Advance the grid one cell every `advanceEveryMeasures` bars.
    if (downbeat && this.phase === 'playing') {
      const measureIndex = Math.floor(beatIndex / 4);
      if (measureIndex > 0 && measureIndex % this.advanceEveryMeasures === 0) {
        this.advanceBricks();
      }
    }

    if (!this.reducedMotion) {
      this.hud.pulseBeat(downbeat);
      this.shake = Math.min(0.1, this.shake + (downbeat ? 0.05 : 0.02));
    }

    this.refreshSampleUi();
  }

  private advanceBricks(): void {
    // Advance every live brick by one full GRID_UNIT so alignment stays clean.
    // Bricks reaching the paddle row = instant game over (regardless of lives
    // remaining) — matches Arkanoid convention where lives cover the ball
    // falling off, and reaching-the-paddle is a hard fail.
    let breach = false;
    for (const brick of this.bricks) {
      if (!brick.alive) continue;
      brick.advanceZ(this.measureAdvance);
      if (brick.z + brick.halfDepth >= this.dangerZ) breach = true;
    }
    if (breach && this.phase === 'playing') {
      this.status = 'Grid crashed the paddle · game over';
      this.audio.lifeLost();
      this.shake = 0.22;
      this.hud.flash('life');
      this.phase = 'finishing';
      this.pendingHits.length = 0;
      void this.autoStopRecording('Song captured');
    }
  }

  private armSequencerStep(row: number, step: number, sample: LoadedSample, rate: number): void {
    const track = this.sequencer[row];
    if (!track) return;
    // Locked rows are "authored" — the player wants their custom pattern
    // preserved, so bricks that would normally arm/refresh a step here are
    // ignored. Manual clicks (toggleStaticNote) still work.
    if (this.library.getMode() === 'sample' && this.library.isRowLocked(row)) return;
    // Per-note lock: any slot the player placed manually (isStatic) is
    // protected from brick arms regardless of row lock. Bricks can still
    // arm/refresh EMPTY slots or slots holding other brick-armed notes.
    if (track[step]?.isStatic) return;
    // Fresh arm resets shelf-life to full — re-hitting a brick keeps the note
    // alive without the player needing to track how many triggers it has left.
    // Slider max (20) is the "forever" sentinel — Infinity never hits zero.
    // Round-based step tiers, mild growth with round so late-game notes hang
    // around a touch longer without dominating (aggressive scaling in R5-8
    // read as repetitive because the same 8-12 armed notes kept looping).
    // With base=8: R1-4 = 8, R5-8 = 10, R9-12 = 12, R13+ = 14.
    const base = this.tuning.noteShelfLife;
    const TIER_MULT = [1, 1.25, 1.5, 1.75];
    const tier = Math.min(TIER_MULT.length - 1, Math.floor((this.round - 1) / 4));
    const life = base >= 20
      ? Infinity
      : Math.max(1, Math.round(base * TIER_MULT[tier]!));
    track[step] = { sample, rate, remaining: life, isStatic: false };
    this.hud.setSeqCellArmed(row, step, true);
    if (this.audio.isRecording()) {
      this.armHistory.push({ time: performance.now() - this.recordStartTime, row, step });
    }
  }

  /** Click on a sequencer cell → toggle a static (no-shelf-life) note using
   *  the row's current sample. Second click on the same cell clears it. Static
   *  notes coexist with brick-armed notes and survive `noteShelfLife` decay. */
  private toggleStaticNote(row: number, step: number, mods: { shift: boolean } = { shift: false }): void {
    const track = this.sequencer[row];
    if (!track) return;
    const existing = track[step];
    // Click semantics for editing the song live:
    //   empty              → place a locked (static) note
    //   locked note        → toggle off (remove)
    //   brick-armed note   → claim it: replace with a locked note using the
    //                        row's current sample
    //   shift-click        → force-clear anything in the slot
    if (existing && (mods.shift || existing.isStatic)) {
      track[step] = null;
      this.hud.setSeqCellArmed(row, step, false);
      this.status = `Cleared row ${row + 1} step ${step + 1}`;
      return;
    }
    const sample = this.library.getSampleForRow(row, this.library.getSystemRowNames());
    if (!sample?.buffer) {
      this.status = 'Load a sample or pick a system voice to place notes';
      return;
    }
    track[step] = { sample, rate: 1, remaining: Infinity, isStatic: true };
    this.hud.setSeqCellArmed(row, step, true);
    void this.audio.unlock();
    // Preview the placed note immediately so the player hears what they wrote.
    this.audio.playOneShotAt(sample, this.audio.now(), 1, 0.9, 'fx', `place:${sample.id}`, this.clock.secondsPerBeat / 4);
    // Placing a note is an intent-to-author signal — auto-lock the row so
    // subsequent brick hits don't overwrite the pattern the player is
    // building. User can still unlock manually via the padlock badge or
    // right-click on the row label.
    if (this.library.getMode() === 'sample' && !this.library.isRowLocked(row)) {
      this.library.toggleRowLock(row);
      this.refreshRowSlotUi();
      this.status = `Wrote row ${row + 1} step ${step + 1} · row auto-locked`;
    } else {
      this.status = `Wrote row ${row + 1} step ${step + 1}`;
    }
  }

  /** Drag-paint variant of toggleStaticNote: mode is fixed for the whole
   *  stroke (arm-only or clear-only), and cells that already match the
   *  target state are skipped so a drag through mixed cells doesn't
   *  double-toggle. Auto-locks the row on first arm just like a click. */
  private paintStaticNote(row: number, step: number, mode: 'arm' | 'clear'): void {
    const track = this.sequencer[row];
    if (!track) return;
    const existing = track[step];
    if (mode === 'clear') {
      if (!existing) return;
      track[step] = null;
      this.hud.setSeqCellArmed(row, step, false);
      return;
    }
    if (existing) return;
    const sample = this.library.getSampleForRow(row, this.library.getSystemRowNames());
    if (!sample?.buffer) return;
    track[step] = { sample, rate: 1, remaining: Infinity, isStatic: true };
    this.hud.setSeqCellArmed(row, step, true);
    if (this.library.getMode() === 'sample' && !this.library.isRowLocked(row)) {
      this.library.toggleRowLock(row);
      this.refreshRowSlotUi();
    }
  }

  private tickSequencer(): void {
    // Fire every 16th-note that crossed since the last frame.
    const currentSixteenth = Math.floor(this.clock.totalBeats * 4);
    // Clock rewinds (e.g. after launch or restart) can leave lastFired ahead — snap it back.
    if (this.lastFiredSixteenth > currentSixteenth) {
      this.lastFiredSixteenth = currentSixteenth - 1;
    }
    // Sample-mode row-slice rotation: every time we cross a measure boundary
    // (one bar = sequencerSteps 16ths), repick which slices the 6 sound slots
    // point at so more of the loaded pool ends up in play over time.
    const measureIndex = Math.floor(currentSixteenth / this.sequencerSteps);
    if (measureIndex !== this.lastMeasureIndex) {
      this.lastMeasureIndex = measureIndex;
      if (this.library.getMode() === 'sample') {
        this.library.reshuffleRowSliceMap(this.rng);
        this.refreshRowSlotUi();
      }
    }
    while (this.lastFiredSixteenth < currentSixteenth) {
      this.lastFiredSixteenth += 1;
      const step = ((this.lastFiredSixteenth % this.sequencerSteps) + this.sequencerSteps) %
        this.sequencerSteps;
      const when = this.audio.now();
      // Row-mono voice + per-row length caps. Most rows stay short so the
      // mix doesn't muddy up; only row 5 gets a real sustained slice, row 4
      // gets a medium bloom, rows 0-3 are tight percs. Whole ladder shrinks
      // with tempo (sqrt of 80/bpm) so higher rounds keep hits crisper as
      // the BPM climbs — long slices at 155 BPM sound very different from
      // long slices at 80 BPM, this compensates.
      const BASE_HOLD = [0.15, 0.15, 0.15, 0.15, 0.5, 1.8];
      const bpmFactor = Math.min(1, Math.sqrt(REFERENCE_BPM / Math.max(60, this.clock.bpm)));
      const ROW_MAX_HOLD = BASE_HOLD.map((h) => h * bpmFactor);
      for (let row = 0; row < this.sequencer.length; row++) {
        const track = this.sequencer[row];
        const entry = track?.[step];
        if (entry?.sample.buffer) {
          this.audio.playOneShotAt(
            entry.sample,
            when,
            entry.rate,
            0.95,
            'song',
            `song:row:${row}`,
            ROW_MAX_HOLD[row],
          );
          this.hud.flashSeqCell(row, step);
          this.headerScene?.pulseNote(row, step);
          // Shelf-life: burn one trigger, drop the note when it runs out so
          // the arrangement keeps shedding notes and doesn't over-crowd.
          // Skip for user-authored static notes — those hold indefinitely
          // until the player clicks them off again.
          if (!entry.isStatic) {
            entry.remaining -= 1;
            if (entry.remaining <= 0 && track) {
              track[step] = null;
              this.hud.setSeqCellArmed(row, step, false);
            }
          }
        }
      }
      this.hud.setSeqPlayhead(step);
      if (this.audio.isRecording()) {
        this.stepHistory.push({ time: performance.now() - this.recordStartTime, step });
      }
    }
  }

  private clearSequencer(): void {
    for (const row of this.sequencer) row.fill(null);
    this.lastFiredSixteenth = -1;
    this.hud.clearSeqCells();
  }

  private resolvePendingHit(hit: PendingHit, when: number, chaos: number): void {
    if (hit.kind === 'oneshot') {
      // Chaos widens pitch spread; at max BPM the sample fires anywhere from ~half to ~1.6× speed.
      const spread = 0.04 + chaos * 0.55;
      const rate = 1 + (this.rng() * 2 - 1) * spread;

      const hitHold = (this.clock.secondsPerBeat / 4) * Math.min(1, 100 / Math.max(60, this.clock.bpm));
      if (hit.sample?.buffer) {
        this.audio.playOneShotAt(hit.sample, when, rate, 1, 'fx', `fx:${hit.sample.id}`, hitHold);
      } else {
        // Synth fallback so first hits are audible even before samples finish loading.
        this.audio.hitBlipAt(when, hit.brickId, chaos);
      }

      // Glitchy stutter: chance grows with tempo. Fires 1–2 quick retriggers off-grid.
      if (chaos > 0.05 && this.rng() < chaos * 0.4) {
        const spb = this.clock.secondsPerBeat;
        const jitter = when + spb * (0.18 + this.rng() * 0.25);
        const rate2 = rate * (0.85 + this.rng() * 0.5);
        if (hit.sample?.buffer) {
          this.audio.playOneShotAt(hit.sample, jitter, rate2, 0.7, 'fx', `fx:${hit.sample.id}`, hitHold);
        } else {
          this.audio.hitBlipAt(jitter, hit.brickId + 3, chaos);
        }
      }
      return;
    }

    // Loop brick: arm / disarm / destroy on the beat.
    if (hit.destroyed || hit.loopDesired === false) {
      this.disarmLoop(hit.loopKey, when);
      const brick = this.bricks.find((b) => b.id === hit.brickId);
      if (brick) brick.loopActive = false;
      return;
    }

    if (hit.loopDesired === true && hit.sample) {
      this.armLoop(hit.loopKey, hit.sample, hit.brickId, when);
      const brick = this.bricks.find((b) => b.id === hit.brickId);
      if (brick) brick.loopActive = true;
    }
  }

  private armLoop(key: string, sample: LoadedSample, brickId: number, when: number): void {
    const spb = this.clock.secondsPerBeat;
    const freeRun = !!sample.buffer && sample.buffer.duration > spb * 1.75;
    this.armedLoops.set(key, { key, sample, brickId, freeRun });
    if (freeRun) {
      this.audio.startLoopAt(key, sample, when);
    } else {
      this.audio.playBeatVoiceAt(key, sample, when);
    }
  }

  private disarmLoop(key: string, when: number): void {
    this.armedLoops.delete(key);
    this.audio.stopLoopAt(key, when);
    this.audio.stopVoice(key, when);
  }

  private updatePaddle(delta: number): void {
    const axis = this.input.getKeyboardAxis();
    let nextX = this.paddle.x;
    // Paddle scales with tempo so it keeps up with the ball as rounds ramp.
    // Ball speed is ~linear in BPM, so we mirror most of that on the paddle
    // side (0.65× the ball ramp) — enough to stay reactive without turning
    // the paddle into a teleport.
    const paddleScale = 1 + (this.clock.bpm / REFERENCE_BPM - 1) * 0.65;
    const speed = this.tuning.paddleSpeed * paddleScale;

    // Effective half-width = min(field, visible viewport). Keeps everything on screen when the
    // game column is narrower than the full field.
    const worldHalfWidth = this.camera.right;
    const boundHalfWidth = Math.min(FIELD.halfWidth, worldHalfWidth);
    const maxX = boundHalfWidth - this.paddle.halfWidth - 0.05;

    if (axis !== 0) {
      nextX += axis * speed * delta;
    } else if (this.input.isPointerDriven()) {
      const norm = this.input.getPaddleNormX();
      // 1:1 with the pointer — no smoothing so the paddle tracks thumb speed
      // exactly. Previous exponential smoothing left a visible trailing gap
      // on fast flicks; snapping keeps it glued to the finger.
      nextX = -worldHalfWidth + norm * 2 * worldHalfWidth;
    }

    this.paddle.setX(THREE.MathUtils.clamp(nextX, -maxX, maxX));
  }

  private stepBall(delta: number): void {
    const steps = 3;
    const stepDt = delta / steps;
    // Clamp ball collision to whichever is narrower — the field or the visible viewport.
    const effectiveField: FieldBounds = {
      ...FIELD,
      halfWidth: Math.min(FIELD.halfWidth, this.camera.right),
    };
    for (let i = 0; i < steps; i++) {
      this.ball.position.addScaledVector(this.ball.velocity, stepDt);

      // Paddle first: catches the ball anywhere in its box before the wall
      // check gets a chance to fire "bottom" for a ball that's already inside
      // the paddle's collision volume.
      if (this.collision.resolvePaddle(this.ball, this.paddle)) {
        this.audio.paddleHit();
        this.paddle.hitFlash();
        this.ball.hitFlash();
        this.syncBallSpeedToBpm();
        this.resetCombo();
      }

      const wall = this.collision.resolveWalls(this.ball, effectiveField);
      if (wall === 'wall') {
        this.audio.wallHit();
        this.ball.hitFlash();
      } else if (wall === 'bottom') {
        this.loseLife();
        return;
      }

      const hit = this.collision.resolveBricks(this.ball, this.bricks);
      if (hit) this.onBrickHit(hit.brick);

      // A brick bounce near the arena wall can push the ball back through the
      // top/side boundary. Re-clamp so the ball never renders outside the field.
      // We deliberately IGNORE 'bottom' here — a brick bounce shouldn't count as
      // a miss. Only the pre-brick wall check (before the ball has been shoved
      // by a collision) is allowed to end a life.
      this.collision.resolveWalls(this.ball, effectiveField);
    }
  }

  /** Keep ball magnitude locked to current BPM-derived speed. */
  private syncBallSpeedToBpm(): void {
    if (!this.ball.launched) return;
    const speed = this.ball.velocity.length();
    if (speed < 0.001) return;
    const target = this.currentBallSpeed();
    this.ball.velocity.multiplyScalar(target / speed);
  }

  private onBrickHit(brick: Brick): void {
    // Sample is the instrument assigned to this specific brick's row.
    const sample =
      this.library.getById(brick.sampleId) ??
      this.library.getSampleForRow(brick.row, this.library.getSystemRowNames());
    const result = brick.hit();
    if (result === 'destroyed') this.headerScene?.explodeBrick(brick.id);
    this.ball.hitFlash();
    // Key by SAMPLE id so multiple bricks sharing the same sample share one voice
    // (prevents stacked concurrent playback = piled-up volume).
    const loopKey = `sample-${sample?.id ?? brick.id}`;

    // Visual + score now; audio waits for the next beat.
    if (brick.kind === 'loop') {
      if (result === 'destroyed') {
        this.awardDestruction(brick, 50);
        this.status = `Queued stop · ${brick.sampleName}`;
        this.queueHit({
          brickId: brick.id,
          kind: 'loop',
          sample,
          loopKey,
          loopDesired: false,
          destroyed: true,
          sampleName: brick.sampleName,
        });
        // Visual disarm immediately so UI matches intent.
        brick.loopActive = false;
        this.hud.flash('loop');
      } else if (result === 'toggled') {
        const desired = !this.armedLoops.has(loopKey) && !this.isLoopPendingOn(loopKey);
        brick.loopActive = desired;
        this.awardScore(20);
        this.status = desired
          ? `Queued loop · ${brick.sampleName}`
          : `Queued mute · ${brick.sampleName}`;
        this.queueHit({
          brickId: brick.id,
          kind: 'loop',
          sample,
          loopKey,
          loopDesired: desired,
          destroyed: false,
          sampleName: brick.sampleName,
        });
        this.hud.flash('loop');
      }
    } else {
      // Fire immediately at the brick's assigned pitch, with a small chaos-scaled detune.
      const chaos = Math.max(0, this.clock.bpm / REFERENCE_BPM - 1);
      const detune = 1 + (this.rng() * 2 - 1) * chaos * 0.15;
      const rate = brick.rate * detune;
      const now = this.audio.now();
      // Brick-hit playback is cut short (~1 sixteenth) so long procedural
      // samples read as percussive hits — matches the sequencer's cutoff.
      const hitHold = (this.clock.secondsPerBeat / 4) * Math.min(1, 100 / Math.max(60, this.clock.bpm));
      if (sample?.buffer) {
        this.audio.playOneShotAt(sample, now, rate, 1, 'fx', `fx:${sample.id}`, hitHold);
      } else {
        this.audio.hitBlipAt(now, brick.id, chaos);
      }
      // Arm one 16th at the CURRENT playhead position — the sequencer is just
      // a grid of triggers. Sample length itself decides whether the note rings
      // short or long.
      const playheadStep = ((Math.floor(this.clock.totalBeats * 4) % this.sequencerSteps) +
        this.sequencerSteps) % this.sequencerSteps;
      if (sample) this.armSequencerStep(brick.row, playheadStep, sample, brick.rate);
      // Bigger bricks are harder to line up cleanly — pay them out accordingly.
      const sizeUnits = Math.max(1, Math.round((brick.halfWidth * 2) / GRID_UNIT));
      const base = sizeUnits * (result === 'destroyed' ? 100 : 25);
      if (result === 'destroyed') {
        this.awardDestruction(brick, base);
      } else {
        this.awardScore(base);
      }
      // Don't clobber the combo banner set by awardDestruction.
      if (this.combo < 2) {
        this.status = `Hit · ${sample?.name ?? 'synth'} · step ${playheadStep + 1}`;
      }
      this.hud.flash('hit');
    }

    this.shake = Math.min(0.18, this.shake + 0.08);
    this.refreshSampleUi();

    if (this.bricks.every((b) => !b.alive)) {
      this.finishSongCleared();
    }
  }

  /** All bricks cleared: end the run as a completed song. Same flow as a
   *  natural game-over so the recording is finalized and the "Song finished"
   *  overlay (with Audio/Video save buttons) appears. */
  private finishSongCleared(): void {
    if (this.phase !== 'playing') return;
    this.phase = 'finishing';
    this.pendingHits.length = 0;
    this.status = 'All bricks cleared · wrapping the song…';
    this.ball.mesh.visible = false;
    this.audio.win();
    this.hud.flash('win');
    if (!this.titleFlashed) {
      this.headerScene?.flashTitle();
      this.titleFlashed = true;
    }
    void this.autoStopRecording('Song captured');
  }

  private isLoopPendingOn(loopKey: string): boolean {
    // If last pending for this key wants on, treat as pending-on.
    for (let i = this.pendingHits.length - 1; i >= 0; i--) {
      const hit = this.pendingHits[i]!;
      if (hit.loopKey !== loopKey) continue;
      return hit.loopDesired === true;
    }
    return false;
  }

  /** Register a destroyed brick, roll the combo forward if it's close in
   *  space + time to the previous kill, and award the multiplied points.
   *  Combo cluster shots (carom chains) pay out up to COMBO_MAX×. */
  private awardDestruction(brick: Brick, basePoints: number): void {
    const now = performance.now() / 1000;
    const withinTime = now - this.lastKillTime <= COMBO_WINDOW_S;
    const dx = brick.x - this.lastKillX;
    const dz = brick.z - this.lastKillZ;
    const withinRadius = Math.hypot(dx, dz) <= COMBO_RADIUS;
    if (this.combo > 0 && withinTime && withinRadius) {
      this.combo = Math.min(COMBO_MAX, this.combo + 1);
    } else {
      this.combo = 1;
    }
    this.lastKillTime = now;
    this.lastKillX = brick.x;
    this.lastKillZ = brick.z;
    const points = basePoints * this.combo;
    this.awardScore(points);
    if (this.combo >= 2) {
      this.status = `Combo ×${this.combo}! · +${points}`;
    }
  }

  private resetCombo(): void {
    this.combo = 0;
    this.lastKillTime = 0;
  }

  /** Bump score and check for the extra-life threshold. Every EXTRA_LIFE_STEP
   *  crossed grants a life so long runs keep buying themselves headroom. */
  private awardScore(delta: number): void {
    this.score += delta;
    while (this.score >= this.nextLifeAt) {
      this.lives += 1;
      this.nextLifeAt += EXTRA_LIFE_STEP;
      this.audio.win();
      this.hud.flash('win');
      this.status = `Extra life! · ${this.lives} lives`;
    }
  }

  private queueHit(hit: PendingHit): void {
    // Coalesce multiple hits on the same loop brick within one beat window.
    if (hit.kind === 'loop') {
      const existing = this.pendingHits.findIndex((p) => p.loopKey === hit.loopKey && p.kind === 'loop');
      if (existing >= 0) {
        this.pendingHits[existing] = hit;
        return;
      }
    }
    this.pendingHits.push(hit);
  }

  private loseLife(): void {
    this.lives -= 1;
    this.audio.lifeLost();
    this.shake = 0.22;
    this.hud.flash('life');
    this.resetCombo();

    if (this.lives <= 0) {
      // Game over: let sequencer + armed loops keep ringing so the last bar of the
      // song gets captured cleanly. autoStopRecording waits, stops audio, then finalizes.
      this.phase = 'finishing';
      this.pendingHits.length = 0;
      this.status = 'You Lost · wrapping the song…';
      this.headerScene?.showLevelBanner('GAME OVER');
      this.refreshSampleUi();
      void this.autoStopRecording('Song captured');
      return;
    }

    // Song keeps looping while the player relaunches. See update() 'lifePause' branch.
    this.phase = 'lifePause';
    this.lifePauseStart = performance.now();
    this.status = `Life lost · ${this.lives} left · Space to relaunch`;
    this.resetBallOnPaddle();
  }

  private clearAllAudio(): void {
    this.pendingHits.length = 0;
    this.armedLoops.clear();
    this.audio.stopAllLoops();
    for (const brick of this.bricks) brick.loopActive = false;
  }

  private resetBallOnPaddle(): void {
    this.ball.resetOnPaddle(this.paddle.x, this.paddle.z, this.paddle.halfDepth);
    this.ball.mesh.scale.setScalar(1);
  }

  /** Continue-after-win: keep score + lives, bump the round so tempo/ball
   *  scale up, and rebuild the grid with a cleared sequencer for a fresh song. */
  private advanceToNextRound(): void {
    this.stopPlayback();
    this.round += 1;
    this.phase = 'ready';
    this.status = `Round ${this.round} · Space / click to launch`;
    this.clock.reset(this.currentRoundBpm());
    this.beatInBar = 0;
    this.clearAllAudio();
    this.rebuildBricks(false); // false = clear sequencer for the new song
    this.ball.mesh.visible = true;
    this.resetBallOnPaddle();
    this.refreshSampleUi();
    // Between-round title card in the header — reuses the countdown reveal
    // pop-in so the tempo bump feels ceremonial.
    this.headerScene?.showLevelBanner(`LEVEL ${this.round}`);
  }

  private resetRun(): void {
    // Any lingering song playback or leftover recorder from the previous run
    // must stop before the new session starts.
    this.stopPlayback();
    this.score = 0;
    this.lives = 3;
    this.nextLifeAt = EXTRA_LIFE_STEP;
    this.resetCombo();
    this.round = 1;
    this.phase = 'ready';
    this.status = 'Move paddle · Space / click to launch';
    this.clock.reset(this.tuning.startBpm);
    this.beatInBar = 0;
    this.clearAllAudio();
    // Death-restart keeps the sequencer notes so the song the player has been
    // building isn't wiped every time they lose.
    this.rebuildBricks(true);
    this.ball.mesh.visible = true;
    this.resetBallOnPaddle();
    // Fresh run gets a fresh NOIZ FX palette + paddle color + rerolled
    // system sounds so each playthrough sounds and looks different.
    // System-sound regeneration is async; refresh the sample UI once the
    // new procedural voices are decoded so the row labels stay accurate.
    this.randomizeFxSliders();
    this.randomizePaddleColor();
    void this.library.randomizeSystemSounds(this.rng).then(() => {
      this.renderSystemSounds(true);
      this.refreshSampleUi();
      this.refreshRowSlotUi();
    });
    this.refreshSampleUi();
  }

  private rebuildBricks(preserveSequencer = false): void {
    // Guard: rebuilding regenerates the entire brick field, so calling this
    // while a run is in progress wipes the player's damaged/destroyed bricks
    // and reads as "the game restarted". Skip in live phases — new sample
    // assignments (row overrides, imported slices, chop-source swaps) already
    // apply to already-live bricks on next hit via getSampleForRow's row-
    // override fallback in onBrickHit.
    const midRun =
      this.phase === 'playing' ||
      this.phase === 'lifePause' ||
      this.phase === 'paused' ||
      this.phase === 'finishing';
    if (midRun) {
      this.refreshRowSlotUi();
      return;
    }

    for (const brick of this.bricks) {
      this.world.remove(brick.mesh);
      brick.dispose();
    }
    this.bricks.length = 0;

    // Between rounds and death-restarts we keep the sequencer so the song the
    // player is building isn't wiped. Only explicit clears (first bootstrap,
    // library mode changes) pass preserveSequencer=false.
    if (!preserveSequencer) {
      this.clearSequencer();
    }
    this.refreshRowSlotUi();

    let id = 0;
    for (let row = 0; row < BRICK_ROWS; row++) {
      const rowZ = GRID_ORIGIN_Z + row * GRID_UNIT;

      // Size pool — always includes singles for variety, but weighted so 2s
      // and 3s dominate for a balanced field (rather than the previous ~33%
      // singles which read as busy/fiddly). Early rounds get slightly fewer
      // singles; R3+ settles at roughly uniform 1/2/3 distribution.
      const singleWeight = this.round >= 3 ? 2 : 1;
      const sizePool = [...Array(singleWeight).fill(1), 2, 2, 3, 3];
      let colCursor = 0;
      while (colCursor < GRID_COLS) {
        const maxSize = Math.min(3, GRID_COLS - colCursor);
        // Pick from the weighted pool, then clamp to whatever fits in the row
        // remainder so we never overrun the right edge.
        const rawSize = sizePool[Math.floor(this.rng() * sizePool.length)]!;
        const size = Math.min(rawSize, maxSize);
        const width = size * GRID_UNIT;
        const x = GRID_ORIGIN_X + colCursor * GRID_UNIT + width / 2;

        // Random instrument per brick — variety across the field so the sequencer builds up
        // varied notes as you break bricks. In folder mode every brick pulls a random file
        // from the loaded folder so 200 samples all get used, not just the first 6.
        // Default (system) set biases toward drums (rows 0-3) over bass/lead (rows 4-5)
        // so songs read more percussive than melodic.
        const rowNames = this.library.getSystemRowNames();
        // Heavily weighted toward drums (rows 0-3 — kick/snare/hat/perc) so
        // the field reads as mostly percussive with occasional bass/lead
        // sprinkles. Old ratio (12/14 ≈ 86% perc) still let too many tonal
        // bricks in; 20/22 ≈ 91% keeps songs grooving.
        const drumBiasPool = [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 3, 3, 3, 3, 3, 4, 5];
        const instrumentIdx = this.library.getMode() === 'folder'
          ? Math.floor(this.rng() * rowNames.length)
          : drumBiasPool[Math.floor(this.rng() * drumBiasPool.length)]!;
        const sampleName = rowNames[instrumentIdx]!;
        const mode = this.library.getMode();
        const override = this.library.getRowOverride(instrumentIdx);
        const assigned = override
          ? override
          : mode === 'folder' || mode === 'sample'
            ? this.library.getRandomSample(this.rng)
            : this.library.getSampleForRow(instrumentIdx, rowNames);
        const palette = ROW_COLORS[instrumentIdx]!;

        const brick = new Brick({
          id,
          x,
          z: rowZ,
          width,
          depth: GRID_UNIT,
          height: 0.32,
          kind: 'oneshot',
          sampleId: assigned?.id ?? `missing-${id}`,
          sampleName: assigned?.name ?? sampleName,
          hits: 1,
          color: palette.color,
          emissive: palette.emissive,
        });
        // Sequencer step = grid column mod loop length; row = instrument for arm targeting.
        brick.sequencerStep = colCursor % this.sequencerSteps;
        brick.row = instrumentIdx;
        // Pitch selection within the instrument's rate pool. System (procedural
        // synth) mode uses column position → scale walks left-to-right, so
        // clearing bricks in order plays actual melodic phrases instead of
        // random pitches. Sample mode keeps random assignment but layers a
        // small chance of an extra pitch shift on top so sliced samples don't
        // all play at native pitch — some slices get transposed for variety.
        const ratePool = ROW_RATE_POOLS[instrumentIdx]!;
        let rate = mode === 'system'
          ? ratePool[colCursor % ratePool.length]!
          : ratePool[Math.floor(this.rng() * ratePool.length)]!;
        if (mode === 'sample' && this.rng() < 0.35) {
          // ~35% of sample bricks get a random pitch shift picked from a
          // musical set: down an octave, down a fifth, up a fifth, up an
          // octave. Keeps most slices native, sprinkles pitched variants in.
          const SLICE_PITCH_SHIFTS = [0.5, 0.6667, 1.5, 2.0];
          rate *= SLICE_PITCH_SHIFTS[Math.floor(this.rng() * SLICE_PITCH_SHIFTS.length)]!;
        }
        brick.rate = rate;
        this.bricks.push(brick);
        this.world.add(brick.mesh);
        id += 1;
        colCursor += size;
      }
    }

    this.bricksTotal = this.bricks.length;
    this.headerScene?.syncBricks(
      this.bricks.map((b) => ({
        id: b.id,
        color: ROW_COLORS[b.row]!.color,
        sizeUnits: Math.max(1, Math.round((b.halfWidth * 2) / GRID_UNIT)),
        row: b.row,
        step: b.sequencerStep,
      })),
    );
  }

  private render(): void {
    this.composer.render();
    if (this.videoRecorder && this.videoRecorder.state === 'recording') {
      this.drawVideoFrame();
    }
  }

  private createScene(): void {
    this.scene.background = this.buildGradientBackground();
    // Fog color tracks the gradient midpoint so distant geometry blends in
    // without flattening back to a single hue.
    this.scene.fog = new THREE.Fog('#0f1424', 24, 48);

    const hemi = new THREE.HemisphereLight('#dfe7ff', '#1a2030', 1.35);
    this.scene.add(hemi);

    const key = new THREE.DirectionalLight('#ffffff', 2.2);
    key.position.set(-4, 14, 6);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 40;
    key.shadow.camera.left = -12;
    key.shadow.camera.right = 12;
    key.shadow.camera.top = 12;
    key.shadow.camera.bottom = -12;
    this.scene.add(key);

    const fill = new THREE.DirectionalLight('#7a9cff', 0.55);
    fill.position.set(6, 8, -4);
    this.scene.add(fill);

    this.world.add(this.createArena());
    this.world.add(this.paddle.mesh);
    this.world.add(this.ball.mesh);
  }

  /** Vertical gradient texture used as scene.background — dark deep-space top
   *  fading to a muted teal/blue at the bottom. Renders once at scene build,
   *  then Three.js samples it for every frame. */
  private buildGradientBackground(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;
    const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
    g.addColorStop(0.0, '#07090f');   // top: near-black with a violet whisper
    g.addColorStop(0.55, '#0d1428');  // middle: muted deep blue
    g.addColorStop(1.0, '#1a2a44');   // bottom (near paddle): lifted teal-blue
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearFilter;
    return tex;
  }

  private createArena(): THREE.Group {
    const arena = new THREE.Group();
    // Floor plane removed — background is just scene.background so only the grid shows through.

    // Grid rendered as a shader plane so lines stay ~1 device-pixel wide with
    // fwidth-based AA at any zoom/DPR — kills the sub-pixel shimmer the old
    // LineSegments grid had as the ball moved. Plane is padded by half a cell
    // on each side so the boundary lines render as interior lines (otherwise
    // half the AA smoothstep falls outside the plane and edge lines look
    // dimmer than interior ones).
    const padCells = 0.5;
    const planeW = (GRID_COLS + padCells * 2) * GRID_UNIT;
    const planeH = (FIELD_ROWS + padCells * 2) * GRID_UNIT;
    const gridGeom = new THREE.PlaneGeometry(planeW, planeH);
    const gridColor = new THREE.Color(IS_MOBILE_LAYOUT ? '#3f5079' : '#2a3552');
    // Very subtle — the grid reads as a whisper under the bricks, not a
    // structural feature. Mobile bumps a touch to survive smaller screens.
    const gridOpacity = IS_MOBILE_LAYOUT ? 0.18 : 0.12;
    const gridMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uColor: { value: gridColor },
        uOpacity: { value: gridOpacity },
        uCells: { value: new THREE.Vector2(GRID_COLS, FIELD_ROWS) },
        uPad: { value: padCells },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        varying vec2 vUv;
        uniform vec3 uColor;
        uniform float uOpacity;
        uniform vec2 uCells;
        uniform float uPad;
        void main() {
          // Remap uv from padded plane back to field-cell coords: cellUv = 0
          // at the field boundary, cellUv = uCells at the opposite boundary.
          vec2 g = vUv * (uCells + vec2(uPad * 2.0)) - vec2(uPad);
          vec2 d = abs(fract(g - 0.5) - 0.5) / fwidth(g);
          float line = 1.0 - min(min(d.x, d.y), 1.0);
          if (line <= 0.001) discard;
          gl_FragColor = vec4(uColor, line * uOpacity);
        }
      `,
    });
    const grid = new THREE.Mesh(gridGeom, gridMat);
    grid.rotation.x = -Math.PI / 2;
    grid.position.set(0, 0.01, (FIELD.top + FIELD.bottom) / 2);
    arena.add(grid);

    // No outer boundary line — canvas aspect now matches the field aspect
    // exactly, so the outline sat flush at the pixel edge and read as an
    // artifact. Bricks and the grid alone communicate the play bounds.
    return arena;
  }

  private recordingUrl: string | null = null;
  private videoUrl: string | null = null;
  private playbackAudio: HTMLAudioElement | null = null;
  /** Offscreen 1280x720 canvas composited with sequencer (left) + game (right). */
  private videoCanvas: HTMLCanvasElement | null = null;
  private videoCtx: CanvasRenderingContext2D | null = null;
  private videoRecorder: MediaRecorder | null = null;
  private videoChunks: Blob[] = [];
  /** Timeline of sequencer arm events during the current recording — drives the Play-back timelapse. */
  private armHistory: Array<{ time: number; row: number; step: number }> = [];
  /** Timeline of every 16th-note fire during the current recording — drives the playhead + note flashes. */
  private stepHistory: Array<{ time: number; step: number }> = [];
  /** performance.now() reference for `armHistory` timestamps. */
  private recordStartTime = 0;
  /** rAF handle for the playback timelapse loop. */
  private playbackRafId = 0;
  /** setInterval handle for the standalone sequencer preview loop. */
  private previewIntervalId: number | null = null;
  private previewStep = 0;
  /** Sample-panel preview state — tracks which sample-list row is currently
   *  playing so the ▶/⏹ toggle knows whether to stop or start. */
  private samplePreviewId: string | null = null;
  private samplePreviewTimer: number | null = null;
  /** Mic-record state for the SAMPLE panel's 🎤 Record button. */
  private micRecorder: MediaRecorder | null = null;
  private micStream: MediaStream | null = null;
  private micStopTimer: number | null = null;

  private clearSamplePreviewTimer(): void {
    if (this.samplePreviewTimer !== null) {
      window.clearTimeout(this.samplePreviewTimer);
      this.samplePreviewTimer = null;
    }
  }

  /** Close the mobile Sound drawer if it's open. Called after a sample loads
   *  (file picker or mic record) so the player is dropped straight back to
   *  the game instead of having to manually tap ✕. Desktop is a no-op since
   *  the panel is a persistent sidebar there. */
  private closeMobileSoundPanel(): void {
    const appEl = document.querySelector<HTMLElement>('#app');
    if (appEl?.getAttribute('data-sound-open') === '1') {
      appEl.removeAttribute('data-sound-open');
      this.syncPanelPause();
    }
  }

  private stopMicRecording(): void {
    if (this.micStopTimer !== null) {
      window.clearTimeout(this.micStopTimer);
      this.micStopTimer = null;
    }
    if (this.micRecorder && this.micRecorder.state !== 'inactive') {
      try { this.micRecorder.stop(); } catch { /* already stopped */ }
    }
  }

  private async autoStartRecording(): Promise<void> {
    if (this.audio.isRecording()) return;
    // Stop any in-flight playback, but keep the previous blob URL + save buttons
    // enabled so the player can still save the last take right up until the new
    // recording finalizes (see finalizeRecording, which overwrites at the end).
    this.stopPlayback();

    const ok = await this.audio.startRecording();
    if (!ok) return;
    this.armHistory = [];
    this.stepHistory = [];
    this.recordStartTime = performance.now();
    const recBtn = document.querySelector<HTMLButtonElement>('#seq-record');
    if (recBtn) {
      recBtn.textContent = '■ Stop';
      recBtn.classList.add('recording');
    }
    this.startVideoRecording();
  }

  private startVideoRecording(): void {
    if (typeof MediaRecorder === 'undefined') return;
    // Keep the previous video URL + save button enabled until the NEW recording
    // finalizes (see finalizeRecording). Player gets a full chance to save.

    // Offscreen canvas — desktop composites 16:9 landscape (sequencer left,
    // game right). Mobile composites 9:16 portrait (header top, game middle,
    // sequencer bottom) so a mobile recording matches what the player sees.
    if (!this.videoCanvas) {
      this.videoCanvas = document.createElement('canvas');
      if (IS_MOBILE_LAYOUT) {
        this.videoCanvas.width = 720;
        this.videoCanvas.height = 1280;
      } else {
        this.videoCanvas.width = 1280;
        this.videoCanvas.height = 720;
      }
      this.videoCtx = this.videoCanvas.getContext('2d');
    }
    if (!this.videoCtx) return;

    const audioStream = this.audio.getSongStream();
    if (!audioStream) return;
    const videoStream = this.videoCanvas.captureStream(30);
    const combined = new MediaStream([
      ...videoStream.getVideoTracks(),
      ...audioStream.getAudioTracks(),
    ]);
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : MediaRecorder.isTypeSupported('video/webm')
          ? 'video/webm'
          : '';
    try {
      this.videoRecorder = new MediaRecorder(
        combined,
        mime ? { mimeType: mime, videoBitsPerSecond: 4_000_000 } : undefined,
      );
    } catch {
      this.videoRecorder = null;
      return;
    }
    this.videoChunks = [];
    this.videoRecorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) this.videoChunks.push(ev.data);
    };
    this.videoRecorder.start(200);
  }

  private stopVideoRecording(): Promise<Blob | null> {
    return new Promise((resolve) => {
      const rec = this.videoRecorder;
      if (!rec || rec.state !== 'recording') {
        resolve(null);
        return;
      }
      rec.onstop = () => {
        const type = rec.mimeType || 'video/webm';
        const blob = new Blob(this.videoChunks, { type });
        this.videoChunks = [];
        this.videoRecorder = null;
        resolve(blob);
      };
      rec.stop();
    });
  }

  private drawVideoFrame(): void {
    const ctx = this.videoCtx;
    const canvas = this.videoCanvas;
    if (!ctx || !canvas) return;
    const W = canvas.width;
    const H = canvas.height;

    ctx.fillStyle = '#0b0d12';
    ctx.fillRect(0, 0, W, H);

    const headerCanvas = document.querySelector<HTMLCanvasElement>('#header-canvas');

    if (IS_MOBILE_LAYOUT) {
      // Portrait 720×1280 stack: header top, game middle, sequencer bottom.
      // Derive each slot's height from the ACTUAL DOM canvas aspect so no
      // slot needs letterbox padding — a hardcoded aspect (like 16:4) left
      // gaps between header, game, and sequencer that read as empty rows.
      const gameAspect = this.canvas.width / this.canvas.height;
      const headerAspect = headerCanvas && headerCanvas.height > 0
        ? headerCanvas.width / headerCanvas.height
        : 4; // fallback if the header canvas hasn't sized yet on first frame
      const headerH = Math.floor(W / headerAspect);
      const gameH = Math.floor(W / gameAspect);
      const gameY = headerH;
      // Sequencer fills whatever's left below the game; enforce a floor so
      // the sequencer stays readable if header + game happen to eat too much.
      const seqH = Math.max(220, H - headerH - gameH);
      const seqY = H - seqH;

      // Header background gradient + canvas draw
      const gradM = ctx.createRadialGradient(
        W / 2, headerH / 2, 0,
        W / 2, headerH / 2, Math.max(W, headerH) * 0.7,
      );
      gradM.addColorStop(0, '#1e2545');
      gradM.addColorStop(0.6, '#0a0e1c');
      gradM.addColorStop(1, '#000000');
      ctx.fillStyle = gradM;
      ctx.fillRect(0, 0, W, headerH);
      if (headerCanvas && headerCanvas.width > 0) {
        ctx.drawImage(headerCanvas, 0, 0, W, headerH);
      }

      // Game canvas — drawn at full width with its native aspect, flush
      // against the header. No letterbox because we sized the slot to match.
      ctx.drawImage(this.canvas, 0, gameY, W, gameH);

      // Sequencer at the bottom.
      this.drawSequencerToVideo(ctx, 12, seqY + 8, W - 24, seqH - 16);
      this.drawVideoWatermark(ctx, W, H);
      return;
    }

    // Desktop 1280×720 layout — sequencer on the left, header+game stacked
    // on the right at the same 20:20 aspect as the in-app view.
    const stackW = Math.floor(H * 20 / 20);
    const stackX = W - stackW;
    const headerH = Math.floor(H * 4 / 20);
    const gameH = H - headerH;

    const grad = ctx.createRadialGradient(
      stackX + stackW / 2, headerH / 2, 0,
      stackX + stackW / 2, headerH / 2, Math.max(stackW, headerH) * 0.7,
    );
    grad.addColorStop(0, '#1e2545');
    grad.addColorStop(0.6, '#0a0e1c');
    grad.addColorStop(1, '#000000');
    ctx.fillStyle = grad;
    ctx.fillRect(stackX, 0, stackW, headerH);
    if (headerCanvas && headerCanvas.width > 0) {
      ctx.drawImage(headerCanvas, stackX, 0, stackW, headerH);
    }
    ctx.drawImage(this.canvas, stackX, headerH, stackW, gameH);

    const seqX = 20;
    const seqY = 20;
    const seqW = stackX - 40;
    const seqH = H - 40;
    this.drawSequencerToVideo(ctx, seqX, seqY, seqW, seqH);
    this.drawVideoWatermark(ctx, W, H);
  }

  /** Bottom-left corner credit "ARKANOIZ MADE BY SPIRITFORM" for every
   *  recording — single line, ARKANOIZ styled like the in-app logo
   *  (ARKA white, NOIZ letters in the row palette) + muted attribution. */
  private drawVideoWatermark(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    void W;
    ctx.save();
    const pad = 14;
    const arkFont = 'bold 20px "Anton", "Impact", "Arial Narrow", sans-serif';
    const byFont = '600 12px "Segoe UI", "Avenir Next", Avenir, sans-serif';
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    const y = H - pad;
    ctx.font = arkFont;
    // Per-letter colored draw so the logo reads like the header title:
    // ARKA in white, then N / O / I / Z in the row palette hexes.
    const logo: Array<[string, string]> = [
      ['ARKA', '#eef2ff'],
      ['N', '#ff1e5a'],
      ['O', '#ff6a1a'],
      ['I', '#ffd60a'],
      ['Z', '#b64bff'],
    ];
    let cursor = pad;
    for (const [glyph, color] of logo) {
      ctx.fillStyle = color;
      ctx.fillText(glyph, cursor, y);
      cursor += ctx.measureText(glyph).width;
    }
    // Tail attribution, muted.
    ctx.font = byFont;
    ctx.fillStyle = 'rgba(238, 242, 255, 0.55)';
    ctx.fillText('   MADE BY SPIRITFORM', cursor, y);
    ctx.restore();
  }

  private drawSequencerToVideo(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
  ): void {
    const rows = this.sequencer.length;
    const cols = this.sequencerSteps;
    const labelW = 60;
    const gridX = x + labelW;
    const gridW = w - labelW;
    const gap = 4;
    const cellW = (gridW - gap * (cols - 1)) / cols;
    const cellH = (h - gap * (rows - 1)) / rows;

    // Row labels — use whatever names the library is currently exposing so
    // the recording tracks Randomize sound renames instead of showing stale
    // KICK/SNARE/etc.
    const labels = this.library.getSystemRowNames().map((n) => n.toUpperCase());
    ctx.font = '600 13px "Segoe UI", Avenir, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(238, 242, 255, 0.55)';
    for (let row = 0; row < rows; row++) {
      const cy = y + row * (cellH + gap) + cellH / 2;
      ctx.fillText(labels[row] ?? '', x, cy);
    }

    // Cells
    const playheadStep =
      this.audio.isRecording() &&
      (this.phase === 'playing' || this.phase === 'lifePause' || this.phase === 'finishing')
        ? ((Math.floor(this.clock.totalBeats * 4) % cols) + cols) % cols
        : -1;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const cx = gridX + col * (cellW + gap);
        const cy = y + row * (cellH + gap);
        const entry = this.sequencer[row]?.[col];
        const armed = !!entry;
        const isDownbeat = col % 4 === 0;
        ctx.fillStyle = armed
          ? this.rowVideoColor(row)
          : isDownbeat
            ? 'rgba(255, 255, 255, 0.07)'
            : 'rgba(255, 255, 255, 0.04)';
        this.roundRect(ctx, cx, cy, cellW, cellH, 3);
        ctx.fill();
        if (col === playheadStep) {
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
          ctx.lineWidth = 1.5;
          this.roundRect(ctx, cx + 0.5, cy + 0.5, cellW - 1, cellH - 1, 3);
          ctx.stroke();
        }
      }
    }
  }

  private rowVideoColor(row: number): string {
    const hex = ['#ff1e5a', '#ff6a1a', '#ffd60a', '#26e884', '#3d5cff', '#b64bff'];
    return hex[row] ?? '#7cf0c2';
  }

  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ): void {
    const rr = Math.min(r, Math.min(w, h) / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }

  private stopPlayback(): void {
    if (this.playbackRafId) {
      cancelAnimationFrame(this.playbackRafId);
      this.playbackRafId = 0;
    }
    if (this.playbackAudio && !this.playbackAudio.paused) {
      this.playbackAudio.pause();
      this.playbackAudio.currentTime = 0;
    }
  }

  private toggleSequencerPreview(): void {
    const playBtn = document.querySelector<HTMLButtonElement>('#seq-play');
    if (this.previewIntervalId !== null) {
      window.clearInterval(this.previewIntervalId);
      this.previewIntervalId = null;
      this.hud.setSeqPlayhead(-1);
      if (playBtn) playBtn.textContent = '▶ Play';
      return;
    }
    // While the game clock is actively ticking, tickSequencer is already
    // firing armed notes on beat. Spawning a second setInterval clock here
    // double-fires each step with a small offset — reads as flanged notes
    // and a visual playhead that drifts from the audio. Refuse to start.
    const gameTicking =
      this.phase === 'playing' ||
      this.phase === 'lifePause' ||
      this.phase === 'finishing';
    if (gameTicking) {
      this.status = 'Sequencer is live with the game — pause to preview';
      return;
    }
    void this.audio.unlock();
    const bpm = Math.max(40, Math.min(300, this.clock.bpm || this.tuning.startBpm));
    const stepSeconds = 60 / bpm / 4;
    this.previewStep = 0;
    const fire = () => {
      const step = this.previewStep;
      const when = this.audio.now() + 0.02;
      for (let row = 0; row < this.sequencer.length; row++) {
        const entry = this.sequencer[row]?.[step];
        if (entry?.sample.buffer) {
          this.audio.playOneShotAt(
            entry.sample, when, entry.rate, 0.95, 'song',
            `song:${entry.sample.id}`, stepSeconds,
          );
          this.hud.flashSeqCell(row, step);
          this.headerScene?.pulseNote(row, step);
        }
      }
      this.hud.setSeqPlayhead(step);
      this.previewStep = (step + 1) % this.sequencerSteps;
    };
    fire();
    this.previewIntervalId = window.setInterval(fire, stepSeconds * 1000);
    if (playBtn) playBtn.textContent = '■ Stop';
  }

/** Tracks how the current recording ended, so the overlay + status can match. */
  private lastStopReason: 'died' | 'stopped' = 'died';

  private async autoStopRecording(_reason: string): Promise<void> {
    // MUST always transition phase out of 'finishing', even when there's no
    // active recording to finalize. The prior early-return here bricked the
    // game: 'finishing' ignores input, so a dropped recording (e.g. audio
    // context suspended, video recorder errored) left the run frozen with
    // no bricks, no ball, and no way to pause.
    const wasRecording = this.audio.isRecording();
    this.lastStopReason = 'died';
    // Wrap-up window — long enough for the "You Lost · wrapping the song…"
    // overlay to read and for the last note tail to ring into the recording.
    // Skip the tail when there's no recording — nothing to capture.
    if (wasRecording) await new Promise((r) => setTimeout(r, 1500));
    if (this.phase === 'finishing') this.phase = 'lost';
    this.audio.stopAllLoops();
    this.armedLoops.clear();
    for (const brick of this.bricks) brick.loopActive = false;
    if (wasRecording) {
      await new Promise((r) => setTimeout(r, 200));
      await this.finalizeRecording('died');
    }
  }

  private async manualStopRecording(): Promise<void> {
    if (!this.audio.isRecording()) return;
    this.lastStopReason = 'stopped';
    // No tail — user hit Stop, cut it now.
    this.phase = 'lost';
    this.pendingHits.length = 0;
    this.audio.stopAllLoops();
    this.armedLoops.clear();
    for (const brick of this.bricks) brick.loopActive = false;
    // Drain any Space / R presses queued during play so entering 'lost' phase
    // doesn't immediately trigger resetRun and start a new game.
    this.input.consumeLaunch();
    this.input.consumeRestart();
    await this.finalizeRecording('stopped');
  }

  private async finalizeRecording(reason: 'died' | 'stopped'): Promise<void> {
    const [audioBlob, videoBlob] = await Promise.all([
      this.audio.stopRecording(),
      this.stopVideoRecording(),
    ]);
    const recBtn = document.querySelector<HTMLButtonElement>('#seq-record');
    const playBtn = document.querySelector<HTMLButtonElement>('#seq-play');
    if (recBtn) {
      recBtn.textContent = '● Record';
      recBtn.classList.remove('recording');
    }
    // Dying with bricks still on the field isn't a finished song — treat it
    // the same as a manual stop so the message + overlay don't over-celebrate.
    const songComplete = reason === 'died' && !this.bricks.some((b) => b.alive);
    if (audioBlob) {
      if (this.recordingUrl) URL.revokeObjectURL(this.recordingUrl);
      this.recordingUrl = URL.createObjectURL(audioBlob);
      if (playBtn) playBtn.disabled = false;
      this.status = songComplete
        ? '🎉 Congratulations — you finished a song! Press ▶ Play to listen.'
        : 'Recording saved · press ▶ Play to listen';
    } else {
      this.status = songComplete
        ? 'Game over · press Space to try again'
        : 'Stopped';
    }
    if (videoBlob) {
      if (this.videoUrl) URL.revokeObjectURL(this.videoUrl);
      this.videoUrl = URL.createObjectURL(videoBlob);
    }
  }

  /** Row currently awaiting a slot-file upload (via #slot-input). */
  private pendingSlotRow: number | null = null;
  /** Stop function for the current chop preview (returned by AudioSystem.playBuffer). */
  private stopChopPreview: (() => void) | null = null;
  /** rAF handle for the chop preview playhead animation. */
  private chopPreviewRafId = 0;
  /** Start time (audio-clock) of the current preview, and its start offset in seconds. */
  private chopPreviewInfo: { started: number; offsetSec: number } | null = null;

  private installUiHandlers(): void {
    this.unbindLibrary = this.library.onChange(() => {
      this.refreshSampleUi();
      const m = this.library.getMode();
      if (m === 'folder' || m === 'sample') this.renderFolderBrowser();
    });

    // Row label → sound slot loader. Click the label to pick an audio file;
    // shift-click to clear the slot back to the mode's default sample.
    this.hud.onSampleDroppedOnRow(async (row, payload) => {
      // Folder-browser (Tauri) drags encode `file:<json>` for unloaded disk
      // files — read + import them on drop, then apply. Loaded samples come
      // through as `id:<sampleId>` (or a raw ID for legacy drag sources).
      let sampleId = payload;
      if (payload.startsWith('file:')) {
        if (!this.folderFs) return;
        try {
          const meta = JSON.parse(payload.slice(5)) as { path: string; rel: string; name: string };
          const bytes = await this.folderFs.readFile(meta.path);
          const f = new File([bytes as unknown as BlobPart], meta.name);
          const before = new Set(this.library.list().map((s) => s.id));
          await this.library.addDroppedTree([{ file: f, relativePath: meta.rel }]);
          const added = this.library.list().find((s) => !before.has(s.id) && s.relativePath === meta.rel);
          if (!added) { this.status = `Failed to import ${meta.name}`; return; }
          sampleId = added.id;
          this.renderFolderBrowser();
        } catch (err) {
          this.status = `Import failed: ${err instanceof Error ? err.message : String(err)}`;
          return;
        }
      } else if (payload.startsWith('id:')) {
        sampleId = payload.slice(3);
      }
      const ok = sampleId === 'chop-source-full'
        ? this.library.setRowOverrideFromChopSource(row)
        : this.library.setRowOverrideFromSample(row, sampleId);
      if (ok) {
        this.rebuildBricks();
        this.refreshRowSlotUi();
        this.status = `Row ${row + 1} slot updated`;
        this.hud.flash('load');
      }
    });
    this.hud.onSamplePlayClicked(async (sampleId) => {
      const sample = this.library.getById(sampleId);
      if (!sample) return;
      await this.audio.unlock();
      // Shared voiceKey keeps preview monophonic — clicking a different sample
      // cuts the previous one instead of stacking N overlapping voices (which
      // was slowing the game when many long samples piled up). Click same
      // sample twice = stop (toggle).
      const key = 'preview:panel';
      const now = this.audio.now();
      const sameActive = this.samplePreviewId === sampleId;
      this.audio.stopVoice(key, now);
      this.clearSamplePreviewTimer();
      if (sameActive) {
        this.samplePreviewId = null;
        this.hud.setSamplePreviewPlaying(null);
        return;
      }
      this.audio.playOneShotAt(sample, now, 1, 1, 'fx', key);
      this.samplePreviewId = sampleId;
      this.hud.setSamplePreviewPlaying(sampleId);
      const durationMs = (sample.buffer?.duration ?? 0) * 1000;
      if (durationMs > 0) {
        this.samplePreviewTimer = window.setTimeout(() => {
          if (this.samplePreviewId === sampleId) {
            this.samplePreviewId = null;
            this.hud.setSamplePreviewPlaying(null);
          }
          this.samplePreviewTimer = null;
        }, durationMs + 50);
      }
    });
    this.hud.onCellClicked((row, step, mods) => this.toggleStaticNote(row, step, mods));
    this.hud.onCellPainted((row, step, mode) => this.paintStaticNote(row, step, mode));
    // Direct-click / right-click on the padlock badge (both fast, no modifier
    // hunting) toggles reshuffle-lock + freezes new brick-armed notes on that
    // row so the player can protect a custom-authored beat mid-game.
    this.hud.onLockClicked((row) => {
      if (this.library.getMode() !== 'sample') return;
      const nowLocked = this.library.toggleRowLock(row);
      this.refreshRowSlotUi();
      this.status = nowLocked
        ? `Row ${row + 1} locked · custom beat protected`
        : `Row ${row + 1} unlocked · brick hits will arm notes here again`;
    });
    this.hud.onRowLabelClicked(async (row, mods) => {
      if (mods.ctrl && this.library.getMode() === 'sample') {
        // Pin this row's current slice so the per-measure reshuffle skips it —
        // useful for locking a kick/snare in place while the rest of the rows
        // keep cycling through fresh slices from the pool.
        const nowLocked = this.library.toggleRowLock(row);
        this.refreshRowSlotUi();
        this.status = nowLocked
          ? `Row ${row + 1} locked · slice stays put`
          : `Row ${row + 1} unlocked · slice will rotate`;
        return;
      }
      if (mods.shift && this.library.getRowOverride(row)) {
        this.library.clearRowOverride(row);
        this.rebuildBricks();
        this.refreshRowSlotUi();
        this.status = `Cleared slot ${row + 1}`;
        return;
      }
      if (mods.alt) {
        const input = document.querySelector<HTMLInputElement>('#slot-input');
        if (!input) return;
        this.pendingSlotRow = row;
        input.value = '';
        input.click();
        return;
      }
      const override = this.library.getRowOverride(row);
      const sample = override ?? this.library.getSampleForRow(row, this.library.getSystemRowNames());
      if (!sample) return;
      await this.audio.unlock();
      this.audio.playOneShotAt(sample, this.audio.now(), 1, 1, 'fx', `preview:row:${row}`);
    });
    const slotInput = document.querySelector<HTMLInputElement>('#slot-input');
    slotInput?.addEventListener('change', async () => {
      const file = slotInput.files?.[0];
      const row = this.pendingSlotRow;
      this.pendingSlotRow = null;
      if (!file || row === null) return;
      await this.audio.unlock();
      const ok = await this.library.loadRowOverride(row, file);
      if (ok) {
        this.rebuildBricks();
        this.refreshRowSlotUi();
        this.status = `Loaded "${file.name}" into row ${row + 1}`;
        this.hud.flash('load');
      }
      slotInput.value = '';
    });

    // Sequencer preview transport — Play loops the current pattern standalone.
    const playBtn = document.querySelector<HTMLButtonElement>('#seq-play');
    playBtn?.addEventListener('click', () => {
      this.toggleSequencerPreview();
    });

    // Live BPM control in the sequencer header — lets the player retune the
    // song without opening settings. Updates the audio clock, delay times,
    // and the tuning baseline so the change survives round advances.
    const bpmInput = document.querySelector<HTMLInputElement>('#seq-bpm');
    if (bpmInput) {
      // Seed with the current effective BPM so the input matches what's playing.
      bpmInput.value = String(Math.round(this.clock.bpm || this.currentRoundBpm()));
      const applyBpm = () => {
        const parsed = Number(bpmInput.value);
        if (!Number.isFinite(parsed)) return;
        const clamped = Math.max(60, Math.min(180, Math.round(parsed)));
        this.clock.bpm = clamped;
        // Persist as the starting BPM so currentRoundBpm() honors it and the
        // top HUD stays in sync via the update-loop publishDiagnostics.
        this.tuning.startBpm = clamped - (this.round - 1) * this.tuning.bpmPerRound;
        this.status = `BPM ${clamped}`;
      };
      bpmInput.addEventListener('change', applyBpm);
      bpmInput.addEventListener('input', applyBpm);
      // Two-way sync: whenever the game's own BPM shifts (round advance,
      // clock.reset on a new run, settings-dial change), push the value into
      // the sequencer input so the two readouts always match. Skip while the
      // input is focused so we don't stomp on mid-edit typing.
      this.seqBpmInput = bpmInput;
    }

    // "Ready to play?" Start button — mirrors the mobile splash's Start:
    // clears the awaiting-start flag, kicks the same countdown → auto-launch
    // that used to run automatically on load.
    const overlayStart = document.querySelector<HTMLButtonElement>('#overlay-start');
    overlayStart?.addEventListener('click', () => {
      if (!this.awaitingStart) return;
      this.awaitingStart = false;
      this.syncGameOverlay();
      this.startCountdown();
      // Must fire from the same user gesture as the click, or the browser
      // rejects the pointer-lock request.
      this.input.requestPointerLock();
    });

    // "Edit sounds" button — opens the Sound drawer so mobile players can
    // tinker with samples/FX before hitting Ready. On desktop the panel is
    // always visible on the left column, so the toggle is effectively a
    // no-op there (the data attribute has no matching desktop CSS).
    const overlayEditSounds = document.querySelector<HTMLButtonElement>('#overlay-edit-sounds');
    overlayEditSounds?.addEventListener('click', () => {
      const appEl = document.querySelector<HTMLElement>('#app');
      if (!appEl) return;
      // Match the mobileSoundBtn open path: close settings first (only one
      // drawer at a time), then flip data-sound-open.
      this.debugTools.setHidden(true);
      appEl.setAttribute('data-sound-open', '1');
      this.syncPanelPause();
    });

    // Pause-overlay actions.
    const overlayContinue = document.querySelector<HTMLButtonElement>('#overlay-continue');
    overlayContinue?.addEventListener('click', () => {
      if (this.phase !== 'paused') return;
      this.phase = 'playing';
      this.status = 'Back in';
      // Trigger the same NOIZ-card unfreeze fade the Space-unpause path uses,
      // otherwise the pinned card sticks around when resuming via the button.
      this.headerScene?.setHeaderEditFrozen(false);
      this.input.requestPointerLock();
    });
    const triggerDownload = (url: string | null, ext: 'wav' | 'webm', prefix = 'arkanoiz') => {
      if (!url) return;
      const filename = `${prefix}-${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`;
      void saveBlobUrl(url, filename).catch((err) => {
        this.status = `Save failed: ${err instanceof Error ? err.message : String(err)}`;
      });
    };
    const overlaySaveAudio = document.querySelector<HTMLButtonElement>('#overlay-save-audio');
    overlaySaveAudio?.addEventListener('click', async () => {
      if (this.phase !== 'paused' && this.phase !== 'playing') return;
      await this.manualStopRecording();
      triggerDownload(this.recordingUrl, 'wav');
    });
    const overlaySaveVideo = document.querySelector<HTMLButtonElement>('#overlay-save-video');
    overlaySaveVideo?.addEventListener('click', async () => {
      if (this.phase !== 'paused' && this.phase !== 'playing') return;
      await this.manualStopRecording();
      triggerDownload(this.videoUrl, 'webm');
    });

    // Song-finished Continue: advance to next round with faster BPM/ball.
    // Score and lives carry over so long runs feel rewarding.
    const overlaySongContinue = document.querySelector<HTMLButtonElement>('#overlay-song-continue');
    overlaySongContinue?.addEventListener('click', () => {
      if (this.phase !== 'lost') return;
      this.advanceToNextRound();
    });
    // Game-over Play again: fresh start — wipe the sequencer so the next run
    // begins with an empty grid rather than replaying the previous notes.
    const overlayPlayAgain = document.querySelector<HTMLButtonElement>('#overlay-play-again');
    overlayPlayAgain?.addEventListener('click', () => {
      if (this.phase !== 'lost') return;
      this.resetRun();
      this.clearSequencer();
    });

    // End-of-game overlay download buttons.
    const overlayDlAudio = document.querySelector<HTMLButtonElement>('#overlay-download-audio');
    overlayDlAudio?.addEventListener('click', () => {
      triggerDownload(this.recordingUrl, 'wav', 'arkanoiz-audio');
    });
    const overlayDlVideo = document.querySelector<HTMLButtonElement>('#overlay-download-video');
    overlayDlVideo?.addEventListener('click', () => {
      triggerDownload(this.videoUrl, 'webm', 'arkanoiz-video');
    });

    // Mode toggle: SYSTEM synth vs. SAMPLE (chop + folder browser combined).
    document.querySelectorAll<HTMLButtonElement>('.mode-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode: 'system' | 'sample' = btn.dataset.mode === 'sample' ? 'sample' : 'system';
        this.library.setMode(mode);
        this.status = mode === 'system'
          ? 'Using system synth'
          : 'Sample mode — browse a folder, slice a sample, drop onto rows';
        this.refreshSampleUi();
        this.refreshRowSlotUi();
        this.syncSamplePanels();
      });
    });

    // Default (system) panel — the sound list is rendered from
    // library.getSystemRowNames() so Randomize can rotate slot names and the
    // list updates in place. Click any name to preview it.
    this.renderSystemSounds();

    // System panel — Randomize regenerates the procedural samples in-place
    // (each row keeps its slot; the tone it plays changes). Any custom
    // row overrides the player loaded from SAMPLE are cleared so the fresh
    // randomized system voices actually play — otherwise the overridden
    // rows would keep the old custom sample and the randomize would feel
    // like it didn't take effect. Sequencer pattern is left alone.
    const systemRandomize = document.querySelector<HTMLButtonElement>('#system-randomize');
    systemRandomize?.addEventListener('click', async () => {
      await this.audio.unlock();
      for (let row = 0; row < 6; row++) this.library.clearRowOverride(row);
      await this.library.randomizeSystemSounds(this.rng);
      this.renderSystemSounds(true);
      this.refreshSampleUi();
      this.refreshRowSlotUi();
      this.status = 'Randomized system sounds';
      this.hud.flash('load');
    });

    // Folder browser wiring.
    const folderBrowseBtn = document.querySelector<HTMLButtonElement>('#folder-browse');
    folderBrowseBtn?.addEventListener('click', async () => {
      // In Tauri, use the native Explorer folder dialog + fs plugin — that
      // path skips Chromium's "Allow this site to view and copy files" popup
      // that showDirectoryPicker triggers under WebView2.
      const tauri = (window as unknown as {
        __TAURI__?: {
          dialog?: { open: (opts: { directory: boolean; multiple: boolean; title?: string }) => Promise<string | string[] | null> };
          fs?: {
            readDir: (path: string) => Promise<Array<{ name: string; isDirectory: boolean; isFile: boolean }>>;
            readFile: (path: string) => Promise<Uint8Array>;
          };
        };
      }).__TAURI__;
      if (tauri?.dialog?.open && tauri.fs?.readDir) {
        try {
          const picked = await tauri.dialog.open({ directory: true, multiple: false, title: 'Select folder' });
          if (!picked || typeof picked !== 'string') return;
          await this.audio.unlock();
          this.folderRoot = picked;
          this.folderFs = tauri.fs;
          this.folderPath = [];
          this.folderEntriesCache.clear();
          this.renderFolderBrowser();
          const rootName = picked.split(/[\\/]/).filter(Boolean).pop() ?? 'folder';
          this.status = `Browsing ${rootName} — open folders, + to import a file, ⬇ to import a folder`;
        } catch (err) {
          this.status = `Folder pick failed: ${err instanceof Error ? err.message : String(err)}`;
        }
        return;
      }

      // Web fallback: File System Access API, then <input webkitdirectory>.
      const win = window as unknown as {
        showDirectoryPicker?: (opts?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>;
      };
      if (typeof win.showDirectoryPicker !== 'function') {
        document.querySelector<HTMLInputElement>('#sample-input')?.click();
        return;
      }
      try {
        const dir = await win.showDirectoryPicker({ mode: 'read' });
        await this.audio.unlock();
        const collected = await this.collectFolderFiles(dir);
        if (collected.length === 0) {
          this.status = 'No audio files found in that folder';
          return;
        }
        const added = await this.library.addDroppedTree(collected);
        if (added > 0) {
          this.renderFolderBrowser();
          this.status = `Loaded ${added} sample${added === 1 ? '' : 's'} · drag onto rows or click Use All`;
          this.hud.flash('load');
        }
      } catch (err) {
        if ((err as { name?: string })?.name !== 'AbortError') {
          this.status = `Folder pick failed: ${err instanceof Error ? err.message : String(err)}`;
        }
      }
    });

    // Preview mute toggle — flips the icon and stops any in-flight preview
    // voice so the current sample cuts immediately when the user mutes.
    const previewToggle = document.querySelector<HTMLButtonElement>('#folder-preview-toggle');
    const syncPreviewToggleUi = () => {
      if (!previewToggle) return;
      previewToggle.textContent = this.folderPreviewMuted ? '🔇' : '🔊';
      previewToggle.classList.toggle('muted', this.folderPreviewMuted);
      previewToggle.title = this.folderPreviewMuted
        ? 'Preview muted — click to enable click-to-preview'
        : 'Click a file to preview it — click here to mute';
    };
    syncPreviewToggleUi();
    previewToggle?.addEventListener('click', () => {
      this.folderPreviewMuted = !this.folderPreviewMuted;
      if (this.folderPreviewMuted) {
        this.audio.stopVoice('preview:folder', this.audio.now());
        this.folderPreviewSeekStop?.();
        this.folderPreviewSeekStop = null;
        if (this.folderPreviewRafId) {
          cancelAnimationFrame(this.folderPreviewRafId);
          this.folderPreviewRafId = 0;
        }
        if (this.folderPreviewBuffer) this.drawFolderPreviewWaveform(this.folderPreviewBuffer);
      }
      syncPreviewToggleUi();
    });

    // Import slices — after a sample is chopped, push each slice into the
    // library as its own draggable sample. Enabled/disabled by syncSliceUi.
    const importSlicesBtn = document.querySelector<HTMLButtonElement>('#folder-import-slices');
    importSlicesBtn?.addEventListener('click', () => {
      const assigned = this.library.importChopSlicesToSequencer();
      if (assigned > 0) {
        this.rebuildBricks();
        this.refreshRowSlotUi();
        this.status = `Applied slices to ${assigned} row${assigned === 1 ? '' : 's'}`;
        this.hud.flash('load');
        // User committed the slices — close the mobile Sound drawer so they're
        // dropped straight back into the game to play with their new sounds.
        this.closeMobileSoundPanel();
      } else {
        this.status = 'Slice a sample first';
      }
    });

    // Slice — takes the currently-previewed folder file, hands it to the
    // chop loader (same code path as SAMPLE's file input), and switches to
    // Sample mode so the sliced view is up front. Disabled until something
    // has been previewed so it's clear what it operates on.
    const folderSliceBtn = document.querySelector<HTMLButtonElement>('#folder-slice');
    folderSliceBtn?.addEventListener('click', async () => {
      if (!this.folderPreviewSelected || !this.folderFs) return;
      folderSliceBtn.disabled = true;
      try {
        const chopCountEl = document.querySelector<HTMLInputElement>('#chop-count');
        const requested = Number(chopCountEl?.value ?? 0);
        await this.sliceCurrentPreview(requested);
      } catch (err) {
        this.status = `Slice failed: ${err instanceof Error ? err.message : String(err)}`;
      } finally {
        folderSliceBtn.disabled = !this.folderPreviewSelected;
      }
    });

    // "Import all" — in Tauri disk-browse mode, walks the current folder,
    // imports every audio file underneath, then applies them to the sequencer.
    // In web/loaded mode, just applies whatever's already been loaded.
    const folderUseAllBtn = document.querySelector<HTMLButtonElement>('#folder-use-all');
    folderUseAllBtn?.addEventListener('click', async () => {
      if (this.folderRoot && this.folderFs) {
        folderUseAllBtn.disabled = true;
        try {
          const rootName = this.folderRoot.split(/[\\/]/).filter(Boolean).pop() ?? 'folder';
          const subAbs = joinFolderPath(this.folderRoot, this.folderPath);
          const relPrefix = [rootName, ...this.folderPath].join('/');
          const collected = await this.walkTauriDir(this.folderFs, subAbs, relPrefix);
          const added = await this.library.addDroppedTree(collected);
          this.status = added > 0
            ? `Imported ${added} sample${added === 1 ? '' : 's'}`
            : 'No audio files found';
        } catch (err) {
          this.status = `Import failed: ${err instanceof Error ? err.message : String(err)}`;
          folderUseAllBtn.disabled = false;
          return;
        }
        folderUseAllBtn.disabled = false;
      }
      this.library.setMode('folder');
      this.rebuildBricks();
      this.refreshRowSlotUi();
      this.syncSamplePanels();
      this.hud.flash('load');
    });

    // Sample-chop panel wiring.
    // Web-only "Load sample" input replaces the folder browser flow when
    // Tauri fs isn't available — routes the picked file straight into the
    // chop loader (same code path as before).
    const chopInput = document.querySelector<HTMLInputElement>('#web-sample-input');
    const chopCount = document.querySelector<HTMLInputElement>('#chop-count');
    const chopCountValue = document.querySelector<HTMLOutputElement>('#chop-count-value');
    const chopReshuffle = document.querySelector<HTMLButtonElement>('#chop-reshuffle');
    chopInput?.addEventListener('change', async () => {
      const file = chopInput.files?.[0];
      if (!file) return;
      await this.audio.unlock();
      // Enforce a floor of 6 so a fresh load always fills all six rows with
      // slices, even if the slider is at 0 or a lower value.
      const requested = Math.max(6, Number(chopCount?.value ?? 6));
      const ok = await this.library.loadChopSource(file, requested, this.rng);
      if (ok) {
        this.library.setMode('sample');
        // Sync the slider with the actual slice count — a one-shot / short
        // sample may get clamped to 1 by rechop's min-slice-size rule, and
        // the slider (still at 12) would misrepresent what's loaded.
        const actual = this.library.getChopState()?.slices.length ?? requested;
        if (chopCount && actual !== requested) {
          chopCount.value = String(actual);
          if (chopCountValue) chopCountValue.value = String(actual);
        }
        this.rebuildBricks();
        this.refreshRowSlotUi();
        this.status = actual === 0
          ? `Loaded "${file.name}" · drag Slices up to chop`
          : actual === 1
            ? `Loaded "${file.name}" · one-shot (drag waveform onto a row to pin)`
            : `Loaded "${file.name}" · sliced into ${actual}`;
        this.hud.flash('load');
        this.drawChopWaveform();
        this.syncSamplePanels();
        const importSlicesBtnRef = document.querySelector<HTMLButtonElement>('#folder-import-slices');
        if (importSlicesBtnRef) importSlicesBtnRef.disabled = false;
        // Keep the mobile panel open after load so the user can preview the
        // waveform and adjust slice count before jumping back to the game.
      }
      chopInput.value = '';
    });

    // Mic-record button: capture up to 15s from the mic and pipe the resulting
    // blob into the same chop-loader path as the file input. Second click
    // stops early. Works on web + Tauri (both support getUserMedia in-webview).
    const recordBtn = document.querySelector<HTMLButtonElement>('#web-sample-record');
    recordBtn?.addEventListener('click', async () => {
      if (this.micRecorder) {
        // Already recording → stop early. onstop handler fires the load.
        this.stopMicRecording();
        return;
      }
      try {
        await this.audio.unlock();
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.micStream = stream;
        const recorder = new MediaRecorder(stream);
        this.micRecorder = recorder;
        const chunks: Blob[] = [];
        recorder.addEventListener('dataavailable', (ev) => {
          if (ev.data && ev.data.size > 0) chunks.push(ev.data);
        });
        recorder.addEventListener('stop', async () => {
          // Tear down mic stream immediately so the browser mic indicator clears.
          this.micStream?.getTracks().forEach((t) => t.stop());
          this.micStream = null;
          this.micRecorder = null;
          if (this.micStopTimer !== null) {
            window.clearTimeout(this.micStopTimer);
            this.micStopTimer = null;
          }
          recordBtn.textContent = '🎤 Record';
          recordBtn.classList.remove('recording');
          if (chunks.length === 0) {
            this.status = 'Recording empty — nothing captured.';
            return;
          }
          const blob = new Blob(chunks, { type: chunks[0]!.type || 'audio/webm' });
          const ext = (blob.type.split('/')[1] || 'webm').split(';')[0];
          const file = new File([blob], `mic-${Date.now()}.${ext}`, { type: blob.type });
          const requested = Math.max(6, Number(chopCount?.value ?? 6) || 6);
          const ok = await this.library.loadChopSource(file, requested, this.rng);
          if (!ok) {
            this.status = 'Recording failed to decode.';
            return;
          }
          this.library.setMode('sample');
          const actual = this.library.getChopState()?.slices.length ?? requested;
          if (chopCount && actual !== requested) {
            chopCount.value = String(actual);
            if (chopCountValue) chopCountValue.value = String(actual);
          }
          this.rebuildBricks();
          this.refreshRowSlotUi();
          this.status = actual > 1
            ? `Recorded · sliced into ${actual}`
            : `Recorded · drag Slices up to chop`;
          this.hud.flash('load');
          this.drawChopWaveform();
          this.syncSamplePanels();
          const importSlicesBtnRef = document.querySelector<HTMLButtonElement>('#folder-import-slices');
          if (importSlicesBtnRef) importSlicesBtnRef.disabled = false;
          // Panel stays open after recording finishes — user can adjust chop
          // count and preview before jumping back to the game.
        });
        recorder.start();
        recordBtn.textContent = '⏹ Stop';
        recordBtn.classList.add('recording');
        this.status = 'Recording… (auto-stops at 15s)';
        this.micStopTimer = window.setTimeout(() => this.stopMicRecording(), 15000);
      } catch (err) {
        this.status = `Mic access failed: ${err instanceof Error ? err.message : String(err)}`;
        this.micRecorder = null;
        this.micStream?.getTracks().forEach((t) => t.stop());
        this.micStream = null;
      }
    });

    chopCount?.addEventListener('input', () => {
      const n = Number(chopCount.value);
      if (chopCountValue) chopCountValue.value = String(n);
    });
    chopCount?.addEventListener('change', async () => {
      const n = Number(chopCount.value);
      // First slice from a preview → full load + rebuild bricks (mode changes,
      // sample pool is new). Re-slice of an already-loaded source → just
      // rechop + redraw waveform + row labels; bricks reuse their chop-<idx>
      // sample IDs so no field-wide rebuild is needed. Skipping rebuildBricks
      // + the onChange folder-refresh is what removes the whole-UI flash.
      if (this.library.getChopState()) {
        this.library.rechop(n, this.rng);
        this.drawChopWaveform();
        this.refreshRowSlotUi();
      } else if (this.folderPreviewSelected && this.folderFs) {
        await this.sliceCurrentPreview(n);
      } else {
        return;
      }
      this.status = n === 0 ? 'Slices cleared' : `Sliced into ${n}`;
      const importSlicesBtnRef = document.querySelector<HTMLButtonElement>('#folder-import-slices');
      if (importSlicesBtnRef) importSlicesBtnRef.disabled = n === 0;
    });
    chopReshuffle?.addEventListener('click', () => {
      const n = Number(chopCount?.value ?? 12);
      this.library.rechop(n, this.rng, 'random');
      this.rebuildBricks();
      this.drawChopWaveform();
      this.refreshRowSlotUi();
      this.status = 'Reshuffled slices';
    });

    // Click the waveform to preview the (FX-processed) sample from that spot.
    // Click again to stop / restart from a new position.
    const waveCanvas = document.querySelector<HTMLCanvasElement>('#chop-waveform');
    // Drag the waveform onto a sequencer row label to pin the whole sample
    // (uncut) as that row's slot override.
    waveCanvas?.addEventListener('dragstart', (ev) => {
      // Only allow dragging when the canvas is showing the chopped source —
      // dragging while a preview is on screen would pin the wrong buffer.
      if (!this.library.getChopState() || this.chopWaveMode !== 'chop') {
        ev.preventDefault();
        return;
      }
      ev.dataTransfer?.setData('text/plain', 'chop-source-full');
      if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'copy';
      waveCanvas.classList.add('dragging');
    });
    waveCanvas?.addEventListener('dragend', () => waveCanvas.classList.remove('dragging'));
    waveCanvas?.addEventListener('click', async (ev) => {
      const rect = waveCanvas.getBoundingClientRect();
      const norm = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      // In preview mode, clicking the waveform toggles the folder-file preview:
      // second click while playing = stop, otherwise (re)start from the
      // clicked position.
      if (this.chopWaveMode === 'preview' && this.folderPreviewBuffer) {
        await this.audio.unlock();
        const buf = this.folderPreviewBuffer;
        if (this.audio.isVoiceActive('preview:folder') || this.folderPreviewSeekStop) {
          this.audio.stopVoice('preview:folder', this.audio.now());
          this.folderPreviewSeekStop?.();
          this.folderPreviewSeekStop = null;
          this.drawFolderPreviewWaveform(buf, 0);
          return;
        }
        const offset = norm * buf.duration;
        this.folderPreviewSeekStop = this.audio.playBuffer(buf, 'fx', offset, 1);
        this.folderPreviewStartCtxTime = this.audio.now() - offset;
        this.drawFolderPreviewWaveform(buf, norm);
        this.startFolderPreviewLoop();
        return;
      }
      const state = this.library.getChopState();
      if (!state) return;
      await this.audio.unlock();
      // Toggle: if a chop preview is already running, second click = stop.
      // Otherwise start a new preview from the clicked position.
      if (this.stopChopPreview) {
        this.stopChopPreviewIfActive();
        this.drawChopWaveform();
        return;
      }
      const offset = norm * state.buffer.duration;
      const stop = this.audio.playBuffer(state.buffer, 'fx', offset, 1);
      this.stopChopPreview = stop;
      const started = this.audio.now();
      this.chopPreviewInfo = { started, offsetSec: offset };
      const tick = () => {
        if (!this.chopPreviewInfo) {
          this.chopPreviewRafId = 0;
          return;
        }
        const elapsed = this.audio.now() - this.chopPreviewInfo.started;
        const pos = this.chopPreviewInfo.offsetSec + elapsed;
        if (pos >= state.buffer.duration) {
          this.stopChopPreviewIfActive();
          this.drawChopWaveform();
          return;
        }
        this.drawChopWaveform(pos / state.buffer.duration);
        this.chopPreviewRafId = requestAnimationFrame(tick);
      };
      this.chopPreviewRafId = requestAnimationFrame(tick);
    });

    // NOIZ FX racks — 3 identical racks (chop panel, folder view, system panel)
    // all wired to the same library FX state.
    // Wire all three racks (chop panel, folder view, system panel) to the same
    // library FX state. Moving any slider updates the audio AND mirrors to the
    // other racks so they all stay in sync.
    const fxKeyList = ['bit', 'ott', 'delay', 'crunch'] as const;
    type FxKey = (typeof fxKeyList)[number];
    const applyFxChange = (fxKey: FxKey, value: number) => {
      // Real-time FX chain on the song bus — hits ALL playback (system,
      // sample, folder). No brick rebuild, no sequencer reset.
      this.audio.setNoizFx({ [fxKey]: value });
      this.drawChopWaveform();
      this.refreshRowSlotUi();
      // Mirror the value to both remaining racks (fx-<key> in the Sample panel,
      // fx3-<key> in the System panel) so switching modes keeps sliders in sync.
      for (const prefix of ['fx-', 'fx3-']) {
        const twin = document.querySelector<HTMLInputElement>(`#${prefix}${fxKey}`);
        if (twin && twin.value !== String(value)) twin.value = String(value);
      }
    };
    for (const prefix of ['fx-', 'fx3-']) {
      for (const key of fxKeyList) {
        const input = document.querySelector<HTMLInputElement>(`#${prefix}${key}`);
        input?.addEventListener('input', () => applyFxChange(key, Number(input.value)));
      }
      const sparkle = document.querySelector<HTMLButtonElement>(`#${prefix}randomize`);
      sparkle?.addEventListener('click', async () => {
        await this.audio.unlock();
        for (const key of fxKeyList) {
          applyFxChange(key, Math.round(this.rng() * 100) / 100);
        }
        this.status = 'Randomized NOIZ FX';
        this.hud.flash('load');
      });
    }

    const input = document.querySelector<HTMLInputElement>('#sample-input');
    input?.addEventListener('change', async () => {
      if (!input.files?.length) return;
      await this.audio.unlock();
      const added = await this.library.addFiles(input.files);
      if (added > 0) {
        // Browsing a folder no longer auto-assigns samples to rows/bricks —
        // that was clobbering any Sample-mode setup the user had going. Files
        // land in the folder browser only; the user drags what they want onto
        // rows or clicks "Use All" to swap the whole set in.
        this.renderFolderBrowser();
        this.status = `Loaded ${added} sample${added === 1 ? '' : 's'} · drag onto rows or click Use All`;
        this.hud.flash('load');
      }
      input.value = '';
    });

    const clearLoops = document.querySelector<HTMLButtonElement>('#clear-loops');
    clearLoops?.addEventListener('click', () => {
      this.clearAllAudio();
      this.status = 'All loops stopped';
      this.refreshSampleUi();
    });

    // Mobile Sound drawer toggle — also closes when the panel header ::after
    // pseudo-element is clicked (see mobile CSS). We toggle a data attribute on
    // #app which CSS uses to slide the panel in/out.
    const mobileSoundBtn = document.querySelector<HTMLButtonElement>('#mobile-sound');
    const appEl = document.querySelector<HTMLElement>('#app');
    const closeMobileSound = () => {
      appEl?.removeAttribute('data-sound-open');
      this.syncPanelPause();
    };
    mobileSoundBtn?.addEventListener('click', () => {
      const open = appEl?.getAttribute('data-sound-open') === '1';
      if (open) closeMobileSound();
      else {
        // Only one drawer open at a time — close settings if it was showing.
        this.debugTools.setHidden(true);
        appEl?.setAttribute('data-sound-open', '1');
        this.syncPanelPause();
      }
    });
    // Tap the panel header on mobile to close (matches the ::after ✕ hint).
    document.querySelector<HTMLElement>('#sample-panel > header')?.addEventListener('click', (e) => {
      if (window.matchMedia('(max-width: 640px), (max-aspect-ratio: 3/4)').matches) {
        // Only close if tap is on the header itself (not something inside like a title text).
        if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'STRONG') {
          closeMobileSound();
        }
      }
    });

    const settingsBtn = document.querySelector<HTMLButtonElement>('#settings-button');
    settingsBtn?.addEventListener('click', () => {
      // Close the sound drawer before opening settings so only one is visible.
      if (appEl?.getAttribute('data-settings-open') !== '1') closeMobileSound();
      this.debugTools.toggle();
      // Settings toggles the attribute inside setHidden — sync after the
      // next frame so we read the post-toggle state.
      requestAnimationFrame(() => this.syncPanelPause());
    });

    // HUD pause button — mirrors Space by queuing both a pause + launch
    // into the input controller so the game loop's phase transition code
    // handles it identically to the keyboard shortcut. Icon flips between
    // ⏸ (playing) and ▶ (paused) based on current phase.
    const pauseBtn = document.querySelector<HTMLButtonElement>('#hud-pause');
    pauseBtn?.addEventListener('click', () => {
      this.input.queuePauseToggle();
    });

    // Master mute toggle in the Sound panel header. Stopping propagation
    // so it doesn't also close the mobile drawer via the header ✕ handler.
    // Icon + inline ON/OFF label both flip so the state reads at a glance.
    const muteBtn = document.querySelector<HTMLButtonElement>('#mute-toggle');
    const muteLabel = document.querySelector<HTMLElement>('#mute-toggle-label');
    muteBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      const next = !this.audio.isMuted();
      this.audio.setMuted(next);
      const icon = next ? '🔇' : '🔊';
      const state = next ? 'OFF' : 'ON';
      // Rebuild content preserving the label span so the CSS hooks still apply.
      muteBtn.innerHTML = `${icon} <span id="mute-toggle-label">${state}</span>`;
      // Keep the local reference fresh — innerHTML swap detaches the old span.
      void muteLabel;
      muteBtn.classList.toggle('muted', next);
      muteBtn.setAttribute('aria-label', next ? 'Unmute audio' : 'Mute audio');
      muteBtn.setAttribute('title', next ? 'Unmute' : 'Mute');
    });

    const volumeSlider = document.querySelector<HTMLInputElement>('#master-volume');
    if (volumeSlider) {
      volumeSlider.value = String(this.audio.getMasterVolume());
      volumeSlider.addEventListener('input', () => {
        this.audio.setMasterVolume(Number(volumeSlider.value));
      });
      volumeSlider.addEventListener('pointerdown', (e) => e.stopPropagation());
    }

    // Drag-and-drop folder / file upload — the browser's Browse-folder dialog
    // triggers a native "Upload N files?" confirm; dropping bypasses that.
    // Row-slot drops still take priority (they preventDefault + stopPropagation
    // in the row label handler before this fires).
    const overlay = document.querySelector<HTMLElement>('#drop-overlay');
    let dragDepth = 0;
    const hasFiles = (ev: DragEvent) => {
      const types = ev.dataTransfer?.types;
      return !!types && Array.from(types).includes('Files');
    };
    window.addEventListener('dragenter', (ev) => {
      if (!hasFiles(ev)) return;
      ev.preventDefault();
      dragDepth += 1;
      overlay?.removeAttribute('hidden');
    });
    window.addEventListener('dragover', (ev) => {
      if (!hasFiles(ev)) return;
      ev.preventDefault();
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    });
    window.addEventListener('dragleave', (ev) => {
      if (!hasFiles(ev)) return;
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) overlay?.setAttribute('hidden', '');
    });
    window.addEventListener('drop', async (ev) => {
      if (!hasFiles(ev)) return;
      ev.preventDefault();
      dragDepth = 0;
      overlay?.setAttribute('hidden', '');
      const entries = await this.collectDroppedEntries(ev.dataTransfer);
      if (entries.length === 0) return;
      await this.audio.unlock();
      const added = await this.library.addDroppedTree(entries);
      if (added > 0) {
        this.library.setMode('folder');
        this.rebuildBricks();
        this.status = `Loaded ${added} sample${added === 1 ? '' : 's'} · folder mode`;
        this.hud.flash('load');
      } else {
        this.status = 'No audio files in that drop';
      }
    });
    overlay?.setAttribute('hidden', '');
  }

  /** Walk a DataTransfer's items (files + directories, recursively via
   *  webkitGetAsEntry) into a flat list of { file, relativePath } tuples. */
  private async collectDroppedEntries(
    dt: DataTransfer | null,
  ): Promise<Array<{ file: File; relativePath: string }>> {
    if (!dt) return [];
    const result: Array<{ file: File; relativePath: string }> = [];
    type FSFile = {
      isFile: true;
      isDirectory: false;
      name: string;
      file: (cb: (f: File) => void, err?: (e: unknown) => void) => void;
    };
    type FSDir = {
      isFile: false;
      isDirectory: true;
      name: string;
      createReader: () => {
        readEntries: (cb: (entries: Array<FSFile | FSDir>) => void, err?: (e: unknown) => void) => void;
      };
    };
    const readAllEntries = (dir: FSDir): Promise<Array<FSFile | FSDir>> =>
      new Promise((resolve) => {
        const reader = dir.createReader();
        const all: Array<FSFile | FSDir> = [];
        const readChunk = () => {
          reader.readEntries((batch) => {
            if (batch.length === 0) resolve(all);
            else {
              all.push(...batch);
              readChunk();
            }
          }, () => resolve(all));
        };
        readChunk();
      });
    const walk = async (entry: FSFile | FSDir, prefix: string): Promise<void> => {
      if (entry.isFile) {
        const file = await new Promise<File | null>((resolve) => {
          entry.file((f) => resolve(f), () => resolve(null));
        });
        if (file) result.push({ file, relativePath: prefix + entry.name });
      } else {
        const children = await readAllEntries(entry);
        const nextPrefix = prefix + entry.name + '/';
        for (const child of children) await walk(child, nextPrefix);
      }
    };
    const items = Array.from(dt.items);
    const walks: Promise<void>[] = [];
    for (const item of items) {
      if (item.kind !== 'file') continue;
      const asEntry = item.webkitGetAsEntry?.() as unknown as FSFile | FSDir | null;
      if (asEntry) walks.push(walk(asEntry, ''));
      else {
        const f = item.getAsFile();
        if (f) result.push({ file: f, relativePath: f.name });
      }
    }
    await Promise.all(walks);
    return result;
  }

  /** Rebuild the Default-panel sound list from the library's current slot
   *  names. Also runs a short highlight-flash so the mix-up after Randomize
   *  reads as visible motion instead of a silent DOM swap. */
  private renderSystemSounds(flash = false): void {
    const list = document.querySelector<HTMLUListElement>('#system-sounds');
    if (!list) return;
    const names = this.library.getSystemRowNames();
    list.innerHTML = '';
    for (let i = 0; i < names.length; i++) {
      const li = document.createElement('li');
      li.className = 'file-item';
      li.textContent = names[i] ?? '';
      const id = `proc-slot-${i}`;
      li.dataset.sampleId = id;
      li.addEventListener('click', async () => {
        const sample = this.library.getById(id);
        if (!sample) return;
        await this.audio.unlock();
        this.audio.playOneShotAt(sample, this.audio.now(), 1, 1, 'fx', 'preview:system');
      });
      if (flash) {
        li.classList.add('flash-in');
        // Stagger the flash across items so the shuffle reads left-to-right.
        li.style.animationDelay = `${i * 40}ms`;
        setTimeout(() => li.classList.remove('flash-in'), 400 + i * 40);
      }
      list.append(li);
    }
  }

  private refreshRowSlotUi(): void {
    const mode = this.library.getMode();
    // In Sample mode the labels reflect the actual slice index the row is
    // pointing at — with 20 slices you might see "13 / 07 / 02 / 18 / 05 / 20",
    // and reshuffle-slices repicks them. System/Folder keep their fixed labels.
    let labels: readonly string[];
    if (mode === 'sample') {
      labels = Array.from({ length: 6 }, (_, r) => {
        const idx = this.library.getRowSliceIndex(r);
        return String(idx + 1).padStart(2, '0');
      });
    } else {
      labels = mode === 'system' ? this.library.getSystemRowNames() : FOLDER_ROW_LABELS;
    }
    this.hud.setSeqRowLabels(labels);
    // Reveals the dim padlock badge on every row label whenever we're in
    // Sample mode — covers both chopped-pool rows and one-shot rows pinned
    // via drag-drop. The gesture stays discoverable at a glance.
    this.hud.setSampleMode(mode === 'sample');
    for (let row = 0; row < 6; row++) {
      const override = this.library.getRowOverride(row);
      this.hud.setRowSlot(row, override ? override.name : null);
      // In Sample (chop) mode, tint the row label + cells with the slice color
      // for the slice currently assigned to this row.
      const rowColor =
        mode === 'sample' && !override
          ? this.library.getSliceColor(this.library.getRowSliceIndex(row))
          : null;
      this.hud.setRowColor(row, rowColor);
      // Reshuffle-lock indicator — only meaningful in sample mode.
      this.hud.setRowLocked(row, mode === 'sample' && this.library.isRowLocked(row));
    }
  }

  private syncSamplePanels(): void {
    const mode = this.library.getMode();
    const chopPanel = document.querySelector<HTMLElement>('#sample-panel-chop');
    const systemPanel = document.querySelector<HTMLElement>('#sample-panel-system');
    const sampleList = document.querySelector<HTMLElement>('#sample-list');
    // Sample panel now holds both the folder browser AND the chop editor —
    // one merged surface for browsing, slicing, and importing.
    const sampleVisible = mode === 'sample' || mode === 'folder';
    if (chopPanel) chopPanel.toggleAttribute('hidden', !sampleVisible);
    if (systemPanel) systemPanel.toggleAttribute('hidden', mode !== 'system');
    if (sampleList) sampleList.style.display = sampleVisible ? '' : 'none';
    if (sampleVisible) this.renderFolderBrowser();
  }

  /** Path segments from root — [] means root, ['drums', 'kicks'] means we're inside drums/kicks/. */
  private folderPath: string[] = [];

  /** When set, folder browser reads from disk via Tauri fs plugin instead of
   *  paging through already-loaded samples. Root is the absolute picked path. */
  private folderRoot: string | null = null;
  private folderFs: TauriFs | null = null;

  /** Click-to-preview state for the folder browser: mute toggle, a cache so
   *  re-clicking a file doesn't re-read + re-decode, and the current selection
   *  so we can highlight the active row. */
  private folderPreviewMuted = false;
  private folderPreviewCache = new Map<string, AudioBuffer>();
  private folderPreviewSelected: string | null = null;
  /** Cached folder listing keyed by "<root>|<subpath>". Reused across
   *  library-change re-renders so a selection change or a rechop doesn't
   *  fire a fresh fs.readDir (which briefly blanks the entries list). */
  private folderEntriesCache = new Map<string, TauriFsEntry[]>();
  private folderPreviewBuffer: AudioBuffer | null = null;
  private folderPreviewStartCtxTime = 0;
  private folderPreviewRafId = 0;
  private folderPreviewSeekStop: (() => void) | null = null;
  /** Tracks whether the merged chop-waveform canvas is currently displaying
   *  a preview (from a folder click) or the chop source (from Slice). Used
   *  by the canvas dragstart to avoid dragging the wrong buffer onto a row. */
  private chopWaveMode: 'empty' | 'preview' | 'chop' = 'empty';

  /** Walk a File System Access DirectoryHandle depth-first and collect every
   *  audio file's {file, relativePath} — matches the shape addDroppedTree
   *  expects so we can reuse that loader for both DnD and picker flows. */
  private async collectFolderFiles(
    dir: FileSystemDirectoryHandle,
    prefix = dir.name,
  ): Promise<Array<{ file: File; relativePath: string }>> {
    const out: Array<{ file: File; relativePath: string }> = [];
    const walk = async (handle: FileSystemDirectoryHandle, pathPrefix: string) => {
      for await (const [name, child] of handle.entries()) {
        const childPath = `${pathPrefix}/${name}`;
        if (child.kind === 'file') {
          try {
            const file = await (child as FileSystemFileHandle).getFile();
            out.push({ file, relativePath: childPath });
          } catch {
            // Skip files we can't read (permissions, etc.)
          }
        } else if (child.kind === 'directory') {
          await walk(child as FileSystemDirectoryHandle, childPath);
        }
      }
    };
    await walk(dir, prefix);
    return out;
  }

  private renderFolderBrowser(): void {
    if (this.folderRoot && this.folderFs) {
      void this.renderFolderBrowserTauri(this.folderRoot, this.folderFs);
      return;
    }
    this.renderFolderBrowserLoaded();
  }

  /** Disk-backed browser used under Tauri desktop. Reads the current subfolder
   *  on demand via the fs plugin so nothing gets imported until the user asks
   *  for it — sidesteps the "Allow this site to view and copy files" popup
   *  Chromium raises for showDirectoryPicker under WebView2. */
  private async renderFolderBrowserTauri(root: string, fs: TauriFs): Promise<void> {
    const breadcrumb = document.querySelector<HTMLElement>('#folder-breadcrumb');
    const entries = document.querySelector<HTMLElement>('#folder-entries');
    const pathInput = document.querySelector<HTMLInputElement>('#folder-path');
    if (!breadcrumb || !entries) return;

    const rootName = root.split(/[\\/]/).filter(Boolean).pop() ?? 'folder';
    const currentAbs = joinFolderPath(root, this.folderPath);
    if (pathInput) pathInput.value = [rootName, ...this.folderPath].join('/');

    // Path input above already shows the current folder — the big heading
    // was redundant. Keep just the back arrow when we're not at the root.
    breadcrumb.innerHTML = '';
    if (this.folderPath.length > 0) {
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'folder-back';
      back.textContent = '←';
      back.title = 'Back';
      back.addEventListener('click', () => {
        this.folderPath.pop();
        this.renderFolderBrowser();
      });
      breadcrumb.append(back);
    }

    const cacheKey = `${root}|${this.folderPath.join('/')}`;
    let list = this.folderEntriesCache.get(cacheKey) ?? null;
    if (!list) {
      // Cold path — show a lightweight placeholder while readDir runs. Warm
      // re-renders (selection change, chop notify) skip this entirely.
      entries.innerHTML = '';
      const loading = document.createElement('li');
      loading.textContent = 'reading folder…';
      loading.style.color = 'rgba(238, 242, 255, 0.35)';
      loading.style.fontStyle = 'italic';
      entries.append(loading);
      try {
        list = await fs.readDir(currentAbs);
      } catch (err) {
        loading.textContent = `read failed: ${err instanceof Error ? err.message : String(err)}`;
        return;
      }
      if (this.folderRoot !== root) return;
      this.folderEntriesCache.set(cacheKey, list);
    }
    entries.innerHTML = '';

    const loadedByRel = new Map<string, LoadedSample>();
    for (const s of this.library.list()) {
      if (s.relativePath) loadedByRel.set(s.relativePath, s);
    }

    const folders = list.filter((e) => e.isDirectory).sort((a, b) => a.name.localeCompare(b.name));
    const files = list
      .filter((e) => e.isFile && FOLDER_AUDIO_EXT.test(e.name))
      .sort((a, b) => a.name.localeCompare(b.name));

    for (const folder of folders) {
      const li = document.createElement('li');
      li.className = 'folder-item';
      li.textContent = folder.name;
      li.addEventListener('click', () => {
        this.folderPath.push(folder.name);
        this.renderFolderBrowser();
      });
      entries.append(li);
    }

    for (const file of files) {
      const rel = [rootName, ...this.folderPath, file.name].join('/');
      const loaded = loadedByRel.get(rel);
      const absPath = joinFolderPath(root, [...this.folderPath, file.name]);
      const li = document.createElement('li');
      li.className = 'file-item';
      li.title = file.name;
      li.textContent = file.name;
      // Every file is draggable, whether imported yet or not — the drop
      // handler reads the disk on drop for unloaded ones. Encoded payload
      // lets the same drop code route loaded vs disk files.
      li.setAttribute('draggable', 'true');
      const payload = loaded
        ? `id:${loaded.id}`
        : `file:${JSON.stringify({ path: absPath, rel, name: file.name })}`;
      li.addEventListener('dragstart', (ev) => {
        ev.dataTransfer?.setData('text/plain', payload);
        if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'copy';
        li.classList.add('dragging');
      });
      li.addEventListener('dragend', () => li.classList.remove('dragging'));
      // Click to preview — plays the file + draws its waveform above the list.
      li.addEventListener('click', () => {
        void this.previewFolderFile(absPath, file.name, loaded?.buffer ?? null);
      });
      if (loaded) li.classList.add('file-loaded');
      if (absPath === this.folderPreviewSelected) li.classList.add('file-selected');
      entries.append(li);
    }

    if (folders.length === 0 && files.length === 0) {
      const empty = document.createElement('li');
      empty.textContent = 'empty folder';
      empty.style.color = 'rgba(238, 242, 255, 0.35)';
      empty.style.fontStyle = 'italic';
      entries.append(empty);
    }
  }

  /** Depth-first walk under Tauri fs, collecting every audio file into the
   *  {file, relativePath} shape addDroppedTree expects. */
  private async walkTauriDir(
    fs: TauriFs,
    dirPath: string,
    relPrefix: string,
  ): Promise<Array<{ file: File; relativePath: string }>> {
    const out: Array<{ file: File; relativePath: string }> = [];
    const walk = async (path: string, rel: string) => {
      let list: TauriFsEntry[];
      try { list = await fs.readDir(path); } catch { return; }
      for (const e of list) {
        const childPath = joinFolderPath(path, [e.name]);
        const childRel = `${rel}/${e.name}`;
        if (e.isDirectory) {
          await walk(childPath, childRel);
        } else if (e.isFile && FOLDER_AUDIO_EXT.test(e.name)) {
          try {
            const bytes = await fs.readFile(childPath);
            out.push({ file: new File([bytes as unknown as BlobPart], e.name), relativePath: childRel });
          } catch { /* skip unreadable files */ }
        }
      }
    };
    await walk(dirPath, relPrefix);
    return out;
  }

  /** Load the currently-previewed disk file as the chop source and slice it
   *  into `count` pieces. Shared by the Slice button and the slider-drag
   *  gesture — both flows should feel identical. */
  private async sliceCurrentPreview(count: number): Promise<void> {
    if (!this.folderPreviewSelected || !this.folderFs) return;
    // Kill the RAF playhead loop + preview voice, but leave the last preview
    // frame + buffer visible so the canvas doesn't blank while readFile runs.
    if (this.folderPreviewRafId) {
      cancelAnimationFrame(this.folderPreviewRafId);
      this.folderPreviewRafId = 0;
    }
    this.folderPreviewSeekStop?.();
    this.folderPreviewSeekStop = null;
    this.audio.stopVoice('preview:folder', this.audio.now());
    const absPath = this.folderPreviewSelected;
    const name = absPath.split(/[\\/]/).filter(Boolean).pop() ?? 'sample';
    const bytes = await this.folderFs.readFile(absPath);
    const file = new File([bytes as unknown as BlobPart], name);
    await this.audio.unlock();
    const ok = await this.library.loadChopSource(file, count, this.rng);
    if (!ok) return;
    // Chop is loaded now — the drawChopWaveform below will overwrite the
    // canvas cleanly, so it's safe to release the preview state.
    this.folderPreviewBuffer = null;
    this.chopWaveMode = 'chop';
    const chopCountEl = document.querySelector<HTMLInputElement>('#chop-count');
    const chopCountOut = document.querySelector<HTMLOutputElement>('#chop-count-value');
    const actual = this.library.getChopState()?.slices.length ?? count;
    // Clamp the slider if rechop reduced the count (short samples cap at 1).
    if (chopCountEl && actual !== count && count > 0) {
      chopCountEl.value = String(actual);
      if (chopCountOut) chopCountOut.value = String(actual);
    }
    this.library.setMode('sample');
    this.rebuildBricks();
    this.refreshRowSlotUi();
    this.status = actual === 0
      ? `Loaded "${name}" · drag Slices up to chop`
      : actual === 1
        ? `Loaded "${name}" · one-shot`
        : `Sliced "${name}" into ${actual}`;
    this.hud.flash('load');
    this.drawChopWaveform();
    this.syncSamplePanels();
    const importSlicesBtn = document.querySelector<HTMLButtonElement>('#folder-import-slices');
    if (importSlicesBtn) importSlicesBtn.disabled = actual === 0;
  }

  /** Click-to-preview a disk file from the folder browser. Reads + decodes
   *  on first play (cached after), draws its waveform, and plays via the fx
   *  bus with a dedicated voice key so a new click cuts the previous preview. */
  private async previewFolderFile(
    absPath: string,
    name: string,
    loadedBuffer: AudioBuffer | null,
  ): Promise<void> {
    if (!this.folderFs) return;
    this.folderPreviewSelected = absPath;
    const sliceBtn = document.querySelector<HTMLButtonElement>('#folder-slice');
    if (sliceBtn) sliceBtn.disabled = false;
    // If a chop is already loaded, the user has established a slice pattern —
    // clicking a new file should reslice it at the same count instead of
    // dropping back to plain preview mode.
    if (this.library.getChopState()) {
      const chopCountEl = document.querySelector<HTMLInputElement>('#chop-count');
      const n = Number(chopCountEl?.value ?? 0);
      await this.sliceCurrentPreview(n);
      return;
    }
    if (this.folderPreviewMuted) return;
    // Re-render just enough to move the .file-selected highlight — cheap
    // since the render itself is a synchronous DOM walk once the disk read
    // has cached the current folder.
    this.renderFolderBrowser();
    try {
      await this.audio.unlock();
      let buffer = loadedBuffer ?? this.folderPreviewCache.get(absPath) ?? null;
      if (!buffer) {
        const bytes = await this.folderFs.readFile(absPath);
        // decode() consumes the buffer, so hand it a proper ArrayBuffer copy
        // (Uint8Array's underlying buffer may be a shared/detached view).
        const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
        buffer = await this.audio.decode(ab);
        this.folderPreviewCache.set(absPath, buffer);
      }
      this.folderPreviewBuffer = buffer;
      const preview: LoadedSample = {
        id: `preview:${absPath}`,
        name,
        url: '',
        buffer,
        isLoopCandidate: false,
        source: 'drop',
      };
      this.folderPreviewSeekStop?.();
      this.folderPreviewSeekStop = null;
      const startAt = this.audio.now();
      this.audio.playOneShotAt(preview, startAt, 1, 1, 'fx', 'preview:folder');
      this.folderPreviewStartCtxTime = startAt;
      this.drawFolderPreviewWaveform(buffer, 0);
      this.startFolderPreviewLoop();
    } catch (err) {
      this.status = `Preview failed: ${err instanceof Error ? err.message : String(err)}`;
    }
  }

  /** Ticks the preview canvas each frame with an updated playhead position.
   *  Auto-stops when the buffer's duration has elapsed so we don't burn RAF
   *  cycles once the preview naturally ends. */
  private startFolderPreviewLoop(): void {
    if (this.folderPreviewRafId) {
      cancelAnimationFrame(this.folderPreviewRafId);
      this.folderPreviewRafId = 0;
    }
    const tick = () => {
      const buf = this.folderPreviewBuffer;
      if (!buf) { this.folderPreviewRafId = 0; return; }
      const elapsed = this.audio.now() - this.folderPreviewStartCtxTime;
      const dur = buf.duration;
      if (elapsed >= dur || elapsed < 0) {
        this.drawFolderPreviewWaveform(buf);
        this.folderPreviewRafId = 0;
        return;
      }
      this.drawFolderPreviewWaveform(buf, elapsed / dur);
      this.folderPreviewRafId = requestAnimationFrame(tick);
    };
    this.folderPreviewRafId = requestAnimationFrame(tick);
  }

  /** Simple min/max waveform for the folder preview canvas — thinner and
   *  less ornate than the chop-slice renderer since there are no regions.
   *  Optional playheadNorm draws a scrolling white line over the wave. */
  private drawFolderPreviewWaveform(buffer: AudioBuffer, playheadNorm?: number): void {
    const canvas = document.querySelector<HTMLCanvasElement>('#chop-waveform');
    if (!canvas) return;
    this.chopWaveMode = 'preview';
    const status = document.querySelector<HTMLElement>('#chop-status');
    if (status && this.folderPreviewSelected) {
      const name = this.folderPreviewSelected.split(/[\\/]/).filter(Boolean).pop() ?? '';
      status.textContent = `Preview: ${name} · click Slice to chop`;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    }
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    const src = buffer.getChannelData(0);
    const step = Math.max(1, Math.floor(src.length / w));
    // Un-played region: brighter mint. Played region (behind the playhead):
    // dim it so the playhead position is obvious even at a glance.
    const playheadX = playheadNorm !== undefined
      ? Math.max(0, Math.min(w, Math.floor(playheadNorm * w)))
      : -1;
    for (let x = 0; x < w; x++) {
      const s0 = x * step;
      const s1 = Math.min(src.length, s0 + step);
      let min = 0;
      let max = 0;
      for (let i = s0; i < s1; i++) {
        const v = src[i] ?? 0;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      const y1 = h / 2 + (min * h) / 2;
      const y2 = h / 2 + (max * h) / 2;
      ctx.fillStyle = playheadX >= 0 && x < playheadX
        ? 'rgba(124, 240, 194, 0.35)'
        : 'rgba(124, 240, 194, 0.85)';
      ctx.fillRect(x, y1, 1, Math.max(1, y2 - y1));
    }
    if (playheadX >= 0) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.fillRect(playheadX, 0, Math.max(1, Math.floor(dpr)), h);
    }
  }

  /** Legacy web-only view: paginates over samples already loaded into the
   *  library (via <input webkitdirectory> or drag-drop) and lets the user
   *  drill through their captured relativePaths. */
  private renderFolderBrowserLoaded(): void {
    const breadcrumb = document.querySelector<HTMLElement>('#folder-breadcrumb');
    const entries = document.querySelector<HTMLElement>('#folder-entries');
    const pathInput = document.querySelector<HTMLInputElement>('#folder-path');
    if (!breadcrumb || !entries) return;

    const samples = this.library
      .list()
      .filter((s) => s.source === 'folder' || s.source === 'drop');

    // Determine root name (first segment of any relativePath). Fall back to empty.
    const rootName =
      samples.find((s) => s.relativePath?.includes('/'))?.relativePath?.split('/')[0] ?? '';
    if (pathInput) {
      pathInput.value = rootName
        ? `${rootName}${this.folderPath.length ? '/' + this.folderPath.join('/') : ''}`
        : '';
    }

    // Compute prefix to filter by. If rootName exists, entries are under `<rootName>/<folderPath>/`.
    const prefix = rootName
      ? [rootName, ...this.folderPath].join('/') + '/'
      : this.folderPath.length
        ? this.folderPath.join('/') + '/'
        : '';

    // Big current-folder heading with a back arrow when we're not at the root.
    breadcrumb.innerHTML = '';
    if (this.folderPath.length > 0) {
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'folder-back';
      back.textContent = '←';
      back.title = 'Back to ' + (this.folderPath[this.folderPath.length - 2] ?? rootName ?? 'root');
      back.addEventListener('click', () => {
        this.folderPath.pop();
        this.renderFolderBrowser();
      });
      breadcrumb.append(back);
    }
    const currentName =
      this.folderPath[this.folderPath.length - 1] ?? rootName ?? 'root';
    const heading = document.createElement('span');
    heading.className = 'folder-heading';
    heading.textContent = currentName;
    breadcrumb.append(heading);

    // Entries at current path: partition into subfolders + files
    const subfolderSet = new Set<string>();
    const fileEntries: typeof samples = [];
    for (const sample of samples) {
      const rel = sample.relativePath ?? '';
      if (!rel.startsWith(prefix)) continue;
      const remainder = rel.slice(prefix.length);
      const slashIdx = remainder.indexOf('/');
      if (slashIdx >= 0) {
        subfolderSet.add(remainder.slice(0, slashIdx));
      } else {
        fileEntries.push(sample);
      }
    }
    const subfolders = Array.from(subfolderSet).sort();

    entries.innerHTML = '';
    for (const folder of subfolders) {
      const li = document.createElement('li');
      li.className = 'folder-item';
      li.textContent = folder;
      li.addEventListener('click', () => {
        this.folderPath.push(folder);
        this.renderFolderBrowser();
      });
      entries.append(li);
    }
    for (const sample of fileEntries.sort((a, b) => a.name.localeCompare(b.name))) {
      const li = document.createElement('li');
      li.className = 'file-item';
      li.textContent = sample.name;
      li.title = sample.name;
      li.setAttribute('draggable', 'true');
      li.dataset.sampleId = sample.id;
      li.addEventListener('dragstart', (ev) => {
        ev.dataTransfer?.setData('text/plain', sample.id);
        if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'copy';
        li.classList.add('dragging');
      });
      li.addEventListener('dragend', () => li.classList.remove('dragging'));
      entries.append(li);
    }
    if (subfolders.length === 0 && fileEntries.length === 0) {
      const empty = document.createElement('li');
      empty.textContent = rootName ? 'empty folder' : 'load a folder to start';
      empty.style.color = 'rgba(238, 242, 255, 0.35)';
      empty.style.fontStyle = 'italic';
      entries.append(empty);
    }
  }

  private stopChopPreviewIfActive(): void {
    if (this.chopPreviewRafId) {
      cancelAnimationFrame(this.chopPreviewRafId);
      this.chopPreviewRafId = 0;
    }
    if (this.stopChopPreview) {
      this.stopChopPreview();
      this.stopChopPreview = null;
    }
    this.chopPreviewInfo = null;
  }

  private drawChopWaveform(playheadNorm?: number): void {
    const canvas = document.querySelector<HTMLCanvasElement>('#chop-waveform');
    const status = document.querySelector<HTMLElement>('#chop-status');
    if (!canvas) return;
    const state = this.library.getChopState();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    }
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    if (!state) {
      // No chop loaded — if we're mid-preview, leave the preview draw alone
      // (chopWaveMode stays 'preview'). Otherwise blank the canvas out.
      if (this.chopWaveMode !== 'preview') this.chopWaveMode = 'empty';
      if (status && this.chopWaveMode !== 'preview') {
        status.textContent = 'Browse a folder, click a file to preview, then Slice.';
      }
      return;
    }
    this.chopWaveMode = 'chop';
    if (status) {
      status.textContent = state.slices.length === 0
        ? `${state.sourceName} · drag Slices up to chop`
        : `${state.sourceName} · ${state.slices.length} slices`;
    }

    // 0 slices — draw the plain source waveform without regions/boundaries.
    if (state.slices.length === 0) {
      const src0 = state.buffer.getChannelData(0);
      const step0 = Math.max(1, Math.floor(src0.length / w));
      ctx.fillStyle = 'rgba(124, 240, 194, 0.85)';
      for (let x = 0; x < w; x++) {
        const s0 = x * step0;
        const s1 = Math.min(src0.length, s0 + step0);
        let min = 0;
        let max = 0;
        for (let i = s0; i < s1; i++) {
          const v = src0[i] ?? 0;
          if (v < min) min = v;
          if (v > max) max = v;
        }
        const y1 = h / 2 + (min * h) / 2;
        const y2 = h / 2 + (max * h) / 2;
        ctx.fillRect(x, y1, 1, Math.max(1, y2 - y1));
      }
      return;
    }

    // Rearrange visually to match playback order: sort regions by playIndex,
    // then lay each region out at the play-slot position. Reshuffle literally
    // slices the waveform and drops the pieces in their new order.
    const src = state.buffer.getChannelData(0);
    const totalDuration = state.buffer.duration;
    const playOrder = state.regions
      .map((r, sourceIdx) => ({ ...r, sourceIdx }))
      .sort((a, b) => a.playIndex - b.playIndex);
    // Precompute play-slot [xStart, xEnd] per region. Slot width is region
    // duration normalized by the SUM of all region durations, so slots always
    // fill the canvas from edge to edge regardless of whether the underlying
    // slice regions cover the whole source or leave gaps / overlap (which
    // the random-in/out slicer does by design).
    const totalSliceNorm = playOrder.reduce(
      (sum, r) => sum + (r.end - r.start),
      0,
    );
    const slots: Array<{ region: typeof playOrder[number]; x0: number; x1: number }> = [];
    let cursor = 0;
    for (const region of playOrder) {
      const durNorm = (region.end - region.start) / Math.max(1e-6, totalSliceNorm);
      const slotX0 = cursor * w;
      const slotX1 = (cursor + durNorm) * w;
      slots.push({ region, x0: slotX0, x1: slotX1 });
      cursor += durNorm;
    }

    // 1) Colored band per slot.
    for (const { region, x0, x1 } of slots) {
      ctx.fillStyle = this.hslWithAlpha(region.color, 0.28);
      ctx.fillRect(Math.floor(x0), 0, Math.max(1, Math.ceil(x1) - Math.floor(x0)), h);
    }

    // 2) Waveform — for each slot, sample the corresponding source region.
    ctx.fillStyle = 'rgba(238, 242, 255, 0.9)';
    for (const { region, x0, x1 } of slots) {
      const startX = Math.floor(x0);
      const endX = Math.ceil(x1);
      const srcStartSample = Math.floor(region.start * totalDuration * (src.length / totalDuration));
      const srcEndSample = Math.floor(region.end * totalDuration * (src.length / totalDuration));
      const srcSpan = Math.max(1, srcEndSample - srcStartSample);
      const pixelSpan = Math.max(1, endX - startX);
      const step = Math.max(1, Math.floor(srcSpan / pixelSpan));
      for (let x = startX; x < endX; x++) {
        const t = (x - startX) / pixelSpan;
        const sStart = srcStartSample + Math.floor(t * srcSpan);
        const sEnd = Math.min(src.length, sStart + step);
        let min = 0;
        let max = 0;
        for (let i = sStart; i < sEnd; i++) {
          const v = src[i] ?? 0;
          if (v < min) min = v;
          if (v > max) max = v;
        }
        const y1 = h / 2 + (min * h) / 2;
        const y2 = h / 2 + (max * h) / 2;
        ctx.fillRect(x, y1, 1, Math.max(1, y2 - y1));
      }
    }

    // 3) Slice boundaries between slots — bright per-region color.
    ctx.lineWidth = 1;
    for (const { region, x1 } of slots) {
      ctx.strokeStyle = this.hslWithAlpha(region.color, 0.75);
      const x = Math.floor(x1);
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, h);
      ctx.stroke();
    }

    // Preview playhead
    if (playheadNorm !== undefined) {
      const px = Math.floor(playheadNorm * w) + 0.5;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, 0);
      ctx.lineTo(px, h);
      ctx.stroke();
    }
  }

  private hslWithAlpha(hslColor: string, alpha: number): string {
    // Turns `hsl(240 80% 60%)` into `hsla(240 80% 60% / <alpha>)`.
    return hslColor.replace(/^hsl/, 'hsla').replace(/\)$/, ` / ${alpha})`);
  }

  private refreshSampleUi(): void {
    const loopingSampleIds = new Set<string>();
    for (const armed of this.armedLoops.values()) {
      loopingSampleIds.add(armed.sample.id);
    }
    const active = this.library.getActiveSample();
    this.hud.renderSamples(
      this.library.list(),
      loopingSampleIds,
      this.library.getMode(),
      active?.name ?? null,
    );
    // Re-apply preview-playing state after the list re-renders so the ⏹ icon
    // survives refresh (renderSamples nukes the innerHTML).
    this.hud.setSamplePreviewPlaying(this.samplePreviewId);
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        if (name === 'active-play') {
          this.resetRun();
          this.clock.reset(this.tuning.startBpm);
          this.ball.launch(0.2, this.currentBallSpeed());
          this.phase = 'playing';
        } else if (name === 'complete') {
          for (const brick of this.bricks) brick.destroy();
          this.phase = 'won';
          this.status = 'Stage clear · Space / click to replay';
        } else {
          console.warn(`Unknown test state: ${name}`);
        }
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
      },
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
      },
    };
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: 0,
      score: this.score,
      targetScore: this.bricksTotal,
      complete: this.phase === 'won',
      player: {
        position: {
          x: this.paddle.x,
          y: this.paddle.mesh.position.y,
          z: this.paddle.z,
        },
        speed: this.ball.velocity.length(),
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }
}
